import React, { useState, useRef, useEffect } from 'react';
import { User, Wallet, Upload, Copy, CheckCircle, AlertCircle, Download, Phone, MapPin, Calendar, Globe, FileText, Edit2, Save, X, Clock, Menu } from 'lucide-react';

// ✅ Import Supabase client (create this file if it doesn't exist)
import { supabase } from '../lib/supabaseClient';

// ✅ Import your existing components and utilities
import { makeAgreementPdf } from '../utils/pdfUtils';
import CustomPhoneInput from './CustomPhoneInput';
import './TermsModal.css';

// Stub for removed LP wallet management (data is managed in Supabase only)
function LPWalletManagement() {
  return null;
}

const FILE_VALIDATION_CONFIG = {
  profileImage: {
    allowedTypes: ['image/jpeg', 'image/png'],
    maxSize: 5 * 1024 * 1024, // 5MB
    errorPrefix: 'Profile image'
  },
  documents: {
    allowedTypes: ['application/pdf', 'image/jpeg', 'image/png'],
    maxSize: 10 * 1024 * 1024, // 10MB
    errorPrefix: 'Document'
  },
  signature: {
    allowedTypes: ['image/png', 'image/jpeg'],
    maxSize: 2 * 1024 * 1024, // 2MB
    errorPrefix: 'Signature'
  }
};

const validateFile = (file, validationType) => {
  if (!file) {
    return { isValid: false, error: 'No file selected' };
  }

  const config = FILE_VALIDATION_CONFIG[validationType];
  if (!config) {
    return { isValid: false, error: 'Invalid validation type' };
  }

  // Check file type
  if (!config.allowedTypes.includes(file.type)) {
    const allowedExtensions = config.allowedTypes
      .map(type => {
        if (type === 'application/pdf') return 'PDF';
        if (type === 'image/jpeg') return 'JPG';
        if (type === 'image/png') return 'PNG';
        return type;
      })
      .join(', ');
    
    return { 
      isValid: false, 
      error: `${config.errorPrefix} must be one of: ${allowedExtensions}` 
    };
  }

  // Check file size
  if (file.size > config.maxSize) {
    const maxSizeMB = (config.maxSize / (1024 * 1024)).toFixed(1);
    const fileSizeMB = (file.size / (1024 * 1024)).toFixed(1);
    
    return { 
      isValid: false, 
      error: `${config.errorPrefix} size (${fileSizeMB}MB) exceeds maximum allowed size of ${maxSizeMB}MB` 
    };
  }

  // Check if file is corrupted (basic check)
  if (file.size === 0) {
    return { 
      isValid: false, 
      error: `${config.errorPrefix} appears to be corrupted or empty` 
    };
  }

  return { isValid: true, error: null };
};

const ProfileSettings = ({ client, userStatus, onStatusUpdate }) => {
  const [activeTab, setActiveTab] = useState('profile');
  const [isEditing, setIsEditing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showRejectionModal, setShowRejectionModal] = useState(false);

  // Keep API base consistent with App.js so /api/* hits Express, not the React dev server
  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

  // Get user info from props or context
  const currentUser = client || userStatus || {
    name: 'User Name',
    email: 'user@example.com',
    id: 'USER123'
  };

  // ✅ FIXED: Get the correct client ID - use temp_client_id or clientId from userStatus
  const getActualClientId = () => {
    if (userStatus?.clientId) {
      return userStatus.clientId; // This should be "ABSU000017"
    }
    if (currentUser?.temp_client_id) {
      return currentUser.temp_client_id; // This should be "ABSU000017"
    }
    if (currentUser?.['Id no']) {
      return currentUser['Id no']; // Fallback
    }
    return currentUser?.id; // Last resort
  };
  
  // Profile form data initialized from props
  const [profileData, setProfileData] = useState({
    firstName: currentUser.name?.split(' ')[0] || '',
    lastName: currentUser.name?.split(' ').slice(1).join(' ') || '',
    email: currentUser.email || '',
    phone: '',
    address: ''
  });
  
  // KYC form data matching AgreementForm.js structure
  const [kycData, setKycData] = useState({
    kycType: '',
    customKycType: '',
    kycDocument: null,
    countryCode: '+1',
    isDrawing: false,
    lastX: 0,
    lastY: 0,
    hasSignature: false,
    uploadedSignature: null,
    termsAccepted: false,
    signatureData: null,
    addressProofDocuments: [],
    idProofDocuments: []
  });
  
  const [errors, setErrors] = useState({});
  
  // Refs
  const fileInputRef = useRef(null);
  const signatureInputRef = useRef(null);
  const signatureCanvasRef = useRef(null);

  const [fullNameInput, setFullNameInput] = useState(`${profileData.firstName} ${profileData.lastName}`.trim());

  // Initialize canvas when component mounts - FIXED: Canvas initialization
  useEffect(() => {
    const canvas = signatureCanvasRef.current;
    if (canvas && activeTab === 'kyc') {
      const ctx = canvas.getContext('2d');
      ctx.strokeStyle = '#000';
      ctx.lineWidth = 2;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      
      // Set canvas size properly to avoid coordinate issues
      const rect = canvas.getBoundingClientRect();
      canvas.width = rect.width * window.devicePixelRatio;
      canvas.height = rect.height * window.devicePixelRatio;
      ctx.scale(window.devicePixelRatio, window.devicePixelRatio);
      
      // Reapply styles after scaling
      ctx.strokeStyle = '#000';
      ctx.lineWidth = 2;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
    }
  }, [activeTab]);

  // Add to your existing state declarations
  const [profileImage, setProfileImage] = useState(null);
  const [profileImageUrl, setProfileImageUrl] = useState(null);
  const profileImageInputRef = useRef(null);

  // Add these functions
const handleProfileImageUpload = (file) => {
  if (!file) return;
  
  const validation = validateFile(file, 'profileImage');
  if (!validation.isValid) {
    setErrors(prev => ({ ...prev, profileImage: validation.error }));
    return;
  }
  
  // Additional validation for images
  const img = new Image();
  img.onload = () => {
    // Image loaded successfully
    const reader = new FileReader();
    reader.onload = () => {
      setProfileImage(file);
      setProfileImageUrl(reader.result);
      setErrors(prev => ({ ...prev, profileImage: '' }));
    };
    reader.onerror = () => {
      setErrors(prev => ({ ...prev, profileImage: 'Failed to read profile image file' }));
    };
    reader.readAsDataURL(file);
  };
  img.onerror = () => {
    setErrors(prev => ({ ...prev, profileImage: 'Profile image file is corrupted or invalid' }));
  };
  img.src = URL.createObjectURL(file);
};

  const removeProfileImage = () => {
    setProfileImage(null);
    setProfileImageUrl(null);
    if (profileImageInputRef.current) {
      profileImageInputRef.current.value = '';
    }
  };

  const getInitials = () => {
    const firstName = profileData.firstName || '';
    const lastName = profileData.lastName || '';
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  };

  // Add authentication check on component mount
  useEffect(() => {
    const checkAuthAndPopulateData = async () => {
      // Check if user is authenticated with Supabase
      const { data: { session }, error } = await supabase.auth.getSession();
      
      if (!session) {
        console.warn('No Supabase session found, user may need to authenticate');
        // You might want to redirect to login or show an auth prompt here
      }
      
      // Populate profile data from userStatus
      if (userStatus?.userData) {
        const userData = userStatus.userData;
        
        setProfileData(prev => ({
          ...prev,
          firstName: userData.firstName || '',
          lastName: userData.lastName || '',
          email: userData.email || '',
          phone: userData.phone || '',
          address: userData.address || ''
        }));
        
        // Update the full name input field
        setFullNameInput(userData.fullname || `${userData.firstName || ''} ${userData.lastName || ''}`.trim());
      }
    };
    
    checkAuthAndPopulateData();
  }, [userStatus]);

  // Get KYC status - matching your existing logic
  const getKYCStatus = () => {
    if (!userStatus) return 'not_submitted';
    return userStatus.kycStatus || 'not_submitted';
  };

  // Get rejection comments if status is rejected
  const getRejectionComments = () => {
    if (userStatus?.kycStatus === 'rejected' && userStatus?.rejectionComments) {
      return userStatus.rejectionComments;
    }
    return null;
  };

  const getKYCStatusInfo = () => {
    const status = getKYCStatus();
    switch (status) {
      case 'not_submitted':
        return {
          title: 'Complete your KYC verification to start investing',
          color: 'text-[#B8B8B8]',
          bgColor: 'bg-gradient-to-r from-[#676DFF]/10 to-[#676DFF]/5',
          borderColor: 'border-[#676DFF]/20',
          icon: AlertCircle,
          canEdit: true,
          buttonText: 'Complete KYC',
          buttonBg: 'bg-[#676DFF]'
        };
      case 'submitted':
      case 'pending':
        return {
          title: 'Your documents are being reviewed by our team',
          color: 'text-[#676DFF]',
          bgColor: 'bg-gradient-to-r from-[#676DFF]/10 to-[#676DFF]/5',
          borderColor: 'border-[#676DFF]/20',
          icon: Clock,
          canEdit: false,
          buttonText: 'Under Review',
          buttonBg: 'bg-[#505050]'
        };
      case 'verified':
        return {
          title: 'Your identity has been successfully verified',
          color: 'text-green-500',
          bgColor: 'bg-gradient-to-r from-green-500/10 to-green-500/5',
          borderColor: 'border-green-500/20',
          icon: CheckCircle,
          canEdit: false,
          buttonText: 'Verified',
          buttonBg: 'bg-green-500'
        };
      case 'rejected':
        return {
          title: 'Your KYC application has been rejected',
          color: 'text-red-500',
          bgColor: 'bg-gradient-to-r from-red-500/10 to-red-500/5',
          borderColor: 'border-red-500/20',
          icon: AlertCircle,
          canEdit: true,
          buttonText: 'View Details',
          buttonBg: 'bg-red-500'
        };
      default:
        return {
          title: 'Please contact support for assistance',
          color: 'text-[#B8B8B8]',
          bgColor: 'bg-[#151515]/50',
          borderColor: 'border-[#505050]',
          icon: AlertCircle,
          canEdit: false,
          buttonText: 'Contact Support',
          buttonBg: 'bg-[#505050]'
        };
    }
  };

  // Replace the uploadFileToSupabase function in ProfileSettings.js
  const uploadFileToSupabase = async (file, folder, fileName) => {
    try {
      let bucketName;
      let filePath;
      
      // ✅ FIXED: Use the correct client ID for file paths
      const actualClientId = getActualClientId();
      
      // Use correct buckets based on file type
      if (folder === 'documents') {
        bucketName = 'kyc-docs';
        filePath = `${actualClientId}/${fileName}`;
      } else if (folder === 'signatures') {
        bucketName = 'kyc-docs';
        filePath = `signatures/${actualClientId}/${fileName}`;
      } else if (folder === 'agreements') {
        bucketName = 'agreement-pdfs';
        filePath = `${actualClientId}/${fileName}`;
      } else {
        bucketName = 'kyc-docs';
        filePath = `${folder}/${actualClientId}/${fileName}`;
      }
      
      console.log(`📁 Uploading to bucket: ${bucketName}, path: ${filePath}`);
      
      const { data, error } = await supabase.storage
        .from(bucketName)
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: true
        });

      if (error) throw error;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from(bucketName)
        .getPublicUrl(filePath);

      console.log(`✅ File uploaded successfully: ${publicUrl}`);
      return publicUrl;
    } catch (error) {
      console.error('Error uploading file:', error);
      throw error;
    }
  };

const handleFileUpload = (documentType, file) => {
  if (!file) return;
  
  const validation = validateFile(file, 'documents');
  if (!validation.isValid) {
    setErrors(prev => ({ ...prev, [documentType]: validation.error }));
    return;
  }
  
  // Handle array-based document types
  if (documentType === 'addressProofDocuments' || documentType === 'idProofDocuments') {
    setKycData(prev => {
      const currentDocs = prev[documentType] || [];
      if (currentDocs.length >= 2) {
        setErrors(prevErr => ({
          ...prevErr,
          [documentType]: 'Maximum 2 documents allowed. Please remove a document before adding a new one.'
        }));
        return prev;
      }
      
      // Check for duplicate file names
      const isDuplicate = currentDocs.some(doc => doc.name === file.name);
      if (isDuplicate) {
        setErrors(prevErr => ({
          ...prevErr,
          [documentType]: `A file named "${file.name}" has already been uploaded. Please rename the file or choose a different one.`
        }));
        return prev;
      }
      
      // Clear errors and add file
      setErrors(prevErr => ({ ...prevErr, [documentType]: '' }));
      return {
        ...prev,
        [documentType]: [...currentDocs, file]
      };
    });
  }
};
  const removeFile = (documentType, index = null) => {
    setKycData(prev => {
      const currentDocs = prev[documentType] || [];
      return {
        ...prev,
        [documentType]: currentDocs.filter((_, i) => i !== index)
      };
    });
  };

  // FIXED: Touch handlers for mobile signature with proper coordinate calculation
  const getCanvasCoordinates = (canvas, clientX, clientY) => {
    const rect = canvas.getBoundingClientRect();
    return {
      x: (clientX - rect.left) * (canvas.width / rect.width) / window.devicePixelRatio,
      y: (clientY - rect.top) * (canvas.height / rect.height) / window.devicePixelRatio
    };
  };

  const handleTouchStart = (e) => {
    e.preventDefault();
    const canvas = signatureCanvasRef.current;
    const touch = e.touches[0];
    const coords = getCanvasCoordinates(canvas, touch.clientX, touch.clientY);
    
    setKycData(prev => ({
      ...prev,
      isDrawing: true,
      lastX: coords.x,
      lastY: coords.y
    }));
  };

  const handleTouchMove = (e) => {
    e.preventDefault();
    if (!kycData.isDrawing) return;
    
    const canvas = signatureCanvasRef.current;
    const ctx = canvas.getContext('2d');
    const touch = e.touches[0];
    const coords = getCanvasCoordinates(canvas, touch.clientX, touch.clientY);
    
    ctx.beginPath();
    ctx.moveTo(kycData.lastX, kycData.lastY);
    ctx.lineTo(coords.x, coords.y);
    ctx.stroke();
    
    setKycData(prev => ({
      ...prev,
      lastX: coords.x,
      lastY: coords.y,
      hasSignature: true
    }));
  };

  const handleTouchEnd = (e) => {
    e.preventDefault();
    setKycData(prev => ({ ...prev, isDrawing: false }));
  };

  // FIXED: Signature canvas handlers with proper coordinate calculation
  const startDrawing = (e) => {
    const canvas = signatureCanvasRef.current;
    const coords = getCanvasCoordinates(canvas, e.clientX, e.clientY);
    
    setKycData(prev => ({
      ...prev,
      isDrawing: true,
      lastX: coords.x,
      lastY: coords.y
    }));
  };

  const draw = (e) => {
    if (!kycData.isDrawing) return;
    
    const canvas = signatureCanvasRef.current;
    const ctx = canvas.getContext('2d');
    const coords = getCanvasCoordinates(canvas, e.clientX, e.clientY);
    
    ctx.beginPath();
    ctx.moveTo(kycData.lastX, kycData.lastY);
    ctx.lineTo(coords.x, coords.y);
    ctx.stroke();
    
    setKycData(prev => ({
      ...prev,
      lastX: coords.x,
      lastY: coords.y,
      hasSignature: true
    }));
  };

  const stopDrawing = () => {
    setKycData(prev => ({ ...prev, isDrawing: false }));
  };

  const clearSignature = () => {
    const canvas = signatureCanvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setKycData(prev => ({ 
      ...prev, 
      hasSignature: false, 
      uploadedSignature: null,
      signatureData: null
    }));
  };

const handleSignatureUpload = (file) => {
  if (!file) return;
  
  const validation = validateFile(file, 'signature');
  if (!validation.isValid) {
    setErrors(prev => ({ ...prev, signature: validation.error }));
    return;
  }
  
  // Additional validation for signature images
  const img = new Image();
  img.onload = () => {
    // Check minimum dimensions for signature
    if (img.width < 100 || img.height < 50) {
      setErrors(prev => ({ 
        ...prev, 
        signature: 'Signature image is too small. Minimum size: 100x50 pixels' 
      }));
      return;
    }
    
    const reader = new FileReader();
    reader.onload = () => {
      setKycData(prev => ({ 
        ...prev, 
        uploadedSignature: file,
        signatureData: reader.result,
        hasSignature: true
      }));
      setErrors(prev => ({ ...prev, signature: '' }));
    };
    reader.onerror = () => {
      setErrors(prev => ({ ...prev, signature: 'Failed to read signature file' }));
    };
    reader.readAsDataURL(file);
  };
  img.onerror = () => {
    setErrors(prev => ({ ...prev, signature: 'Signature file is corrupted or invalid' }));
  };
  img.src = URL.createObjectURL(file);
};

  const removeSignature = () => {
    setKycData(prev => ({ 
      ...prev, 
      uploadedSignature: null,
      signatureData: null,
      hasSignature: false
    }));
    if (signatureInputRef.current) {
      signatureInputRef.current.value = '';
    }
  };

  // Get signature data for submission
  const getSignatureData = () => {
    if (kycData.uploadedSignature && kycData.signatureData) {
      return kycData.signatureData;
    }
    
    if (kycData.hasSignature && signatureCanvasRef.current) {
      return signatureCanvasRef.current.toDataURL('image/png');
    }
    
    return null;
  };

  // Convert file to base64
  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = error => reject(error);
    });
  };

  // Generate PDF using your existing pdfUtils
  const generateAgreementPDF = async () => {
    try {
      const formDataForPdf = {
        fullName: `${profileData.firstName} ${profileData.lastName}`.trim(),
        email: profileData.email,
        phone: `${kycData.countryCode}${profileData.phone}`,
        address: profileData.address,
        effectiveDate: new Date().toLocaleDateString('en-GB', {
          day: 'numeric', month: 'long', year: 'numeric'
        })
      };
      
      const signatureData = getSignatureData();
      
      // Use your existing makeAgreementPdf function
      const pdfBlob = await makeAgreementPdf({
        formData: formDataForPdf,
        signatureData: signatureData,
        signatureIsUpload: !!kycData.uploadedSignature
      });
      
      return pdfBlob;
    } catch (error) {
      console.error('Error generating PDF:', error);
      throw error;
    }
  };

  // Form validation
const validateForm = () => {
  const newErrors = {};
  
  // Profile validation
  if (!profileData.firstName.trim()) {
    newErrors.firstName = 'First name is required and cannot be empty';
  } else if (profileData.firstName.trim().length < 2) {
    newErrors.firstName = 'First name must be at least 2 characters long';
  } else if (!/^[a-zA-Z\s'-]+$/.test(profileData.firstName.trim())) {
    newErrors.firstName = 'First name can only contain letters, spaces, hyphens, and apostrophes';
  }
  
  if (!profileData.lastName.trim()) {
    newErrors.lastName = 'Last name is required and cannot be empty';
  } else if (profileData.lastName.trim().length < 2) {
    newErrors.lastName = 'Last name must be at least 2 characters long';
  } else if (!/^[a-zA-Z\s'-]+$/.test(profileData.lastName.trim())) {
    newErrors.lastName = 'Last name can only contain letters, spaces, hyphens, and apostrophes';
  }
  
  if (!profileData.email.trim()) {
    newErrors.email = 'Email address is required';
  } else {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(profileData.email.trim())) {
      newErrors.email = 'Please enter a valid email address (e.g., user@example.com)';
    }
  }
  
  if (!profileData.phone.trim()) {
    newErrors.phone = 'Phone number is required';
  } else {
    const cleanPhone = profileData.phone.replace(/\D/g, '');
    if (cleanPhone.length < 7) {
      newErrors.phone = 'Phone number is too short (minimum 7 digits)';
    } else if (cleanPhone.length > 15) {
      newErrors.phone = 'Phone number is too long (maximum 15 digits)';
    } else if (!/^\d{7,15}$/.test(cleanPhone)) {
      newErrors.phone = 'Phone number must contain only digits';
    }
  }
  
  if (!profileData.address.trim()) {
    newErrors.address = 'Residential address is required';
  } else if (profileData.address.trim().length < 10) {
    newErrors.address = 'Address must be at least 10 characters long';
  }
  
  // Document validation
  if (!kycData.addressProofDocuments || kycData.addressProofDocuments.length === 0) {
    newErrors.addressProofDocuments = 'At least one address proof document is required (utility bill, bank statement, or lease agreement)';
  }
  
  if (!kycData.idProofDocuments || kycData.idProofDocuments.length === 0) {
    newErrors.idProofDocuments = 'At least one ID proof document is required (passport, driver\'s license, or national ID)';
  }
  
  // Signature validation
  const signatureData = getSignatureData();
  if (!kycData.hasSignature && !signatureData) {
    newErrors.signature = 'Digital signature is required. Please either draw your signature or upload a signature image';
  }
  
  // Terms validation
  if (!kycData.termsAccepted) {
    newErrors.terms = 'You must read and accept the terms and conditions to proceed';
  }
  
  setErrors(newErrors);
  return Object.keys(newErrors).length === 0;
};

  // Handle profile save
  const handleProfileSave = async () => {
    setIsSubmitting(true);
    try {
      // Add your profile update API call here
      // const response = await fetch('/api/client/profile', {
      //   method: 'PUT',
      //   headers: {
      //     'Content-Type': 'application/json',
      //     'Authorization': `Bearer ${token}`
      //   },
      //   body: JSON.stringify(profileData)
      // });
      
      setTimeout(() => {
        setIsSubmitting(false);
        setIsEditing(false);
      }, 1000);
    } catch (error) {
      console.error('Error updating profile:', error);
      setIsSubmitting(false);
    }
  };

  // FIXED: KYC Submit with better error handling and debugging
  const handleKYCSubmit = async () => {
    console.log('🚀 KYC Submit button clicked');
    
setErrors(prev => ({ ...prev, submit: '' }));

if (!validateForm()) {
  console.log('❌ Form validation failed', errors);
  setErrors(prev => ({ 
    ...prev, 
    submit: 'Please fix the errors above before submitting' 
  }));
  return;
}
    setIsSubmitting(true);
    
    try {
      const actualClientId = getActualClientId();
      console.log('📝 Starting KYC submission for client:', actualClientId);
      
      // Get authentication token
      const token = localStorage.getItem('token');
      if (!token) {
        console.warn('⚠️ No authentication token found, continuing without token...');
      }
      
      // 1. Upload Address Proof documents
      console.log('📤 Uploading address proof documents...');
      const addressProofUrls = [];
      for (let i = 0; i < kycData.addressProofDocuments.length; i++) {
        const doc = kycData.addressProofDocuments[i];
        try {
          const url = await uploadFileToSupabase(
            doc,
            'documents',
            `address_proof_${i + 1}_${Date.now()}.${doc.name.split('.').pop()}`
          );
          addressProofUrls.push(url);
        } catch (uploadError) {
          console.error(`❌ Failed to upload address proof document ${i + 1}:`, uploadError);
          throw new Error(`Failed to upload address proof document: ${uploadError.message}`);
        }
      }
      console.log('✅ Address proof documents uploaded:', addressProofUrls);
      
      // 2. Upload ID Proof documents
      console.log('📤 Uploading ID proof documents...');
      const idProofUrls = [];
      for (let i = 0; i < kycData.idProofDocuments.length; i++) {
        const doc = kycData.idProofDocuments[i];
        try {
          const url = await uploadFileToSupabase(
            doc,
            'documents',
            `id_proof_${i + 1}_${Date.now()}.${doc.name.split('.').pop()}`
          );
          idProofUrls.push(url);
        } catch (uploadError) {
          console.error(`❌ Failed to upload ID proof document ${i + 1}:`, uploadError);
          throw new Error(`Failed to upload ID proof document: ${uploadError.message}`);
        }
      }
      console.log('✅ ID proof documents uploaded:', idProofUrls);
      
      // 3. Upload signature
      console.log('📤 Processing signature...');
      const signatureData = getSignatureData();
      let signatureUrl = null;
      
      if (signatureData) {
        try {
          const response = await fetch(signatureData);
          const blob = await response.blob();
          
          signatureUrl = await uploadFileToSupabase(
            blob,
            'signatures',
            `signature_${Date.now()}.png`
          );
          console.log('✅ Signature uploaded:', signatureUrl);
        } catch (signatureError) {
          console.error('❌ Failed to upload signature:', signatureError);
          throw new Error(`Failed to upload signature: ${signatureError.message}`);
        }
      }
      
      // 4. Generate and upload PDF
      console.log('📄 Generating PDF...');
      let pdfUrl = null;
      try {
        const pdfBlob = await generateAgreementPDF();
        pdfUrl = await uploadFileToSupabase(
          pdfBlob,
          'agreements',
          `agreement_${Date.now()}.pdf`
        );
        console.log('✅ PDF uploaded:', pdfUrl);
      } catch (pdfError) {
        console.error('❌ Failed to generate/upload PDF:', pdfError);
        throw new Error(`Failed to generate agreement PDF: ${pdfError.message}`);
      }
      
      // 5. Prepare submission data
      const submissionData = {
        clientId: actualClientId,
        fullname: `${profileData.firstName} ${profileData.lastName}`.trim(),
        email: profileData.email,
        countrycode: kycData.countryCode,
        phone: profileData.phone,
        address: profileData.address,
        effectivedate: new Date().toISOString().split('T')[0],
        signature: signatureUrl,
        kyctype: kycData.kycType === 'Others' ? kycData.customKycType : kycData.kycType,
        address_proof_urls: JSON.stringify(addressProofUrls),
        id_proof_urls: JSON.stringify(idProofUrls),
        agreement_pdf_url: pdfUrl
      };
      
      console.log('📤 Submitting to API:', submissionData);
      
      // 6. Submit to API (with fallback if API doesn't exist)
      try {
        const response = await fetch(`${API_URL}/api/submit-kyc`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token && { 'Authorization': `Bearer ${token}` })
          },
          body: JSON.stringify(submissionData)
        });
        
        console.log('📥 API Response status:', response.status);
        
        if (!response.ok) {
          const errorText = await response.text();
          console.error('❌ API Error Response:', {
            status: response.status,
            statusText: response.statusText,
            body: errorText
          });
          
          // If API endpoint doesn't exist, continue with database update
          if (response.status === 404) {
            console.warn('⚠️ API endpoint not found, proceeding with database update only');
          } else {
            throw new Error(`Server error: ${response.status} - ${errorText}`);
          }
        } else {
          const result = await response.json();
          console.log('📥 API Response data:', result);
        }
      } catch (apiError) {
        console.warn('⚠️ API submission failed, continuing with database update:', apiError.message);
        // Don't throw here, continue with database update
      }
      
      // 7. Update KYC status in database with proper authentication
      console.log('🔄 Updating KYC status to submitted...');
      try {
        // Check if we have an authenticated session
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        
        if (!session) {
          console.warn('⚠️ No authenticated session found. User needs to be logged in to update KYC status.');
          throw new Error('User must be authenticated to update KYC status');
        }
        
        console.log('✅ Authenticated session found, updating database...');
        
        // Update the signup_requests table - RLS policy will ensure user can only update their own row
        const { data, error: updateError } = await supabase
          .from('signup_requests')
          .update({ 
            kyc_status: 'submitted',
            kyc_submitted_at: new Date().toISOString()
          })
          .eq('temp_client_id', actualClientId)
          .select();
        
        if (updateError) {
          console.error('❌ Error updating signup_requests table:', updateError);
          throw updateError;
        } else if (!data || data.length === 0) {
          // This usually means the row wasn't matched (wrong id) or RLS prevented the update.
          console.warn('⚠️ No signup_requests rows updated (kyc_status may still be not_submitted).', {
            temp_client_id: actualClientId
          });
        } else {
          console.log('✅ KYC status updated successfully:', data);
        }
        
      } catch (dbError) {
        console.error('❌ Database update failed:', dbError);
        
        // If database update fails, the KYC submission was still successful
        // You might want to show a warning that status update failed but submission succeeded
        console.warn('⚠️ KYC submission succeeded but status update failed. This may need manual review.');
      }
      
      // Success - show custom success message
      console.log('🎉 KYC submitted successfully');
      setShowSuccessMessage(true);
      
      // Update user status
      if (onStatusUpdate) {
        console.log('🔄 Refreshing user status...');
        try {
          await onStatusUpdate();
        } catch (statusError) {
          console.warn('⚠️ Failed to refresh user status:', statusError);
        }
      }
      
      // Switch to profile tab after a delay
      setTimeout(() => {
        setActiveTab('profile');
        setShowSuccessMessage(false);
      }, 3000);
      
} catch (error) {
  console.error('❌ KYC Submission Error:', error);
  
  // Provide user-friendly error messages based on error type
  let userFriendlyMessage = 'An unexpected error occurred during submission. ';
  
  if (error.message.includes('upload')) {
    userFriendlyMessage = 'File upload failed. Please check your internet connection and try again. ';
  } else if (error.message.includes('network')) {
    userFriendlyMessage = 'Network error. Please check your internet connection and try again. ';
  } else if (error.message.includes('authentication')) {
    userFriendlyMessage = 'Authentication error. Please log out and log back in, then try again. ';
  } else if (error.message.includes('validation')) {
    userFriendlyMessage = 'Please review your information and ensure all required fields are completed correctly. ';
  }
  
  setErrors({ 
    submit: userFriendlyMessage + 'If the problem persists, please contact support with error code: ' + Date.now() 
  });
} finally {
  setIsSubmitting(false);
  console.log('🏁 KYC submission process completed');
}
  };

  // Handle download PDF
  const handleDownloadPdf = async () => {
    try {
      const pdfBlob = await generateAgreementPDF();
      const url = URL.createObjectURL(pdfBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Agreement_${profileData.firstName}_${profileData.lastName}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading PDF:', error);
    }
  };

  const kycStatusInfo = getKYCStatusInfo();

  // FIXED: Professional Terms Modal Component
  const TermsModalContent = () => {
    const formDataForTerms = {
      fullName: `${profileData.firstName} ${profileData.lastName}`.trim(),
      email: profileData.email,
      phone: `${kycData.countryCode}${profileData.phone}`,
      address: profileData.address,
      effectiveDate: new Date().toLocaleDateString('en-GB', {
        day: 'numeric', month: 'long', year: 'numeric'
      })
    };

    return (
      <div className="bg-white text-gray-900 p-6 rounded-lg font-['Lexend']">
        <div className="mb-6 text-center border-b pb-4">
          <h1 className="text-2xl font-semibold text-gray-900 mb-2">INVESTOR ADVISORY AGREEMENT</h1>
          <p className="text-gray-600 text-sm">Effective Date: {formDataForTerms.effectiveDate}</p>
        </div>

        <div className="space-y-6">
          {/* Client Information */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-gray-900 mb-3">Client Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
              <div>
                <span className="font-medium text-gray-700">Name:</span>
                <span className="ml-2 text-gray-900">{formDataForTerms.fullName}</span>
              </div>
              <div>
                <span className="font-medium text-gray-700">Email:</span>
                <span className="ml-2 text-gray-900">{formDataForTerms.email}</span>
              </div>
              <div>
                <span className="font-medium text-gray-700">Phone:</span>
                <span className="ml-2 text-gray-900">{formDataForTerms.phone}</span>
              </div>
              <div className="md:col-span-2">
                <span className="font-medium text-gray-700">Address:</span>
                <span className="ml-2 text-gray-900">{formDataForTerms.address}</span>
              </div>
            </div>
          </div>

          {/* Agreement Terms */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">Terms and Conditions</h3>
            
            <div className="prose prose-sm max-w-none text-gray-700 space-y-4">
              <p className="font-medium text-gray-800">
                This Investor Advisory Agreement ("Agreement") is entered into between the Client named above 
                and our investment advisory services. By signing this agreement, you acknowledge and agree to the following terms:
              </p>

              <div className="space-y-4">
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">1. Investment Advisory Services</h4>
                  <p className="text-sm leading-relaxed">We will provide investment advisory services including portfolio management, investment recommendations, and ongoing monitoring of your investments based on your risk profile and investment objectives. Our services include asset allocation strategies, regular portfolio reviews, and investment research to help you achieve your financial goals.</p>
                </div>

                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">2. Risk Disclosure and Investment Suitability</h4>
                  <p className="text-sm leading-relaxed">All investments involve risk, including the potential loss of principal. Past performance does not guarantee future results. Market conditions, economic factors, and individual security performance can affect your investment returns. We will work with you to understand your risk tolerance, investment timeline, and financial objectives to develop an appropriate investment strategy.</p>
                </div>

                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">3. Fees and Charges</h4>
                  <p className="text-sm leading-relaxed">Advisory fees will be clearly disclosed and agreed upon before any services begin. Our fee structure may include management fees, performance fees, or transaction costs depending on the services provided. All costs and potential charges will be transparently communicated to you in writing, and you will receive regular statements detailing all fees charged to your account.</p>
                </div>

                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">4. Client Responsibilities and Communication</h4>
                  <p className="text-sm leading-relaxed">You agree to provide accurate and complete financial information, notify us promptly of any changes in your financial situation, investment objectives, or risk tolerance. You are responsible for reviewing all account statements and trade confirmations, and reporting any discrepancies within a reasonable time frame.</p>
                </div>

                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">5. Confidentiality and Data Protection</h4>
                  <p className="text-sm leading-relaxed">We maintain strict confidentiality regarding your personal and financial information. Your data is protected under industry-standard security measures and regulatory requirements. We will not disclose your information to third parties without your explicit consent, except as required by law or regulatory authorities.</p>
                </div>

                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">6. Regulatory Compliance and Fiduciary Duty</h4>
                  <p className="text-sm leading-relaxed">Our advisory services are provided in compliance with applicable securities laws and regulations. We are committed to acting in your best interests as a fiduciary, which means we will always put your interests ahead of our own and provide advice that is suitable for your particular circumstances.</p>
                </div>

                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">7. Investment Strategy and Objectives</h4>
                  <p className="text-sm leading-relaxed">Your investment strategy will be tailored to your specific financial goals, risk tolerance, and time horizon. We may recommend diversified portfolios including equities, bonds, alternative investments, and other securities as appropriate. Investment decisions will be made based on thorough research and analysis of market conditions.</p>
                </div>

                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">8. Reporting and Account Monitoring</h4>
                  <p className="text-sm leading-relaxed">You will receive regular reports on your portfolio performance, including detailed statements of holdings, transactions, and account values. We will continuously monitor your investments and provide periodic reviews to ensure your portfolio remains aligned with your investment objectives.</p>
                </div>

                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">9. Termination of Agreement</h4>
                  <p className="text-sm leading-relaxed">Either party may terminate this agreement with written notice. Upon termination, we will provide you with a final account statement and assist with the orderly transfer of your assets. Any earned but unpaid fees will be prorated to the date of termination.</p>
                </div>

                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">10. Dispute Resolution</h4>
                  <p className="text-sm leading-relaxed">Any disputes arising from this agreement will be resolved through binding arbitration in accordance with the rules of the relevant financial industry regulatory authority. This agreement shall be governed by the laws of the jurisdiction in which our services are provided.</p>
                </div>

                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">11. Amendments and Modifications</h4>
                  <p className="text-sm leading-relaxed">This agreement may only be amended in writing and signed by both parties. We will provide you with advance notice of any material changes to our services, fees, or terms of engagement.</p>
                </div>
              </div>

              <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <p className="text-sm text-blue-800 font-medium">
                  <strong>Important Notice:</strong> Please read this agreement carefully and in its entirety. By signing below, you acknowledge that you have read, understood, and agree to be bound by all provisions outlined above. If you have any questions about these terms, please contact us before signing.
                </p>
              </div>

              <div className="mt-6 p-4 bg-amber-50 rounded-lg border border-amber-200">
                <h5 className="font-semibold text-amber-800 mb-2">Investment Risks Acknowledgment</h5>
                <p className="text-xs text-amber-700 leading-relaxed">
                  You acknowledge that you understand investing involves risks, including potential loss of principal, and that no investment strategy can guarantee profits or protect against losses. You have disclosed your complete financial situation, investment experience, and objectives, and you understand that our recommendations are based on the information you have provided.
                </p>
              </div>

              {/* Fee Structure Section */}
              <div className="mt-6 border-t pt-6">
                <h4 className="text-lg font-semibold text-gray-900 mb-4">Fee Structure</h4>
                
                <div className="space-y-4">
                  <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                    <h5 className="font-semibold text-gray-900 mb-3 flex items-center">
                      <span className="w-2 h-2 bg-[#676DFF] rounded-full mr-2"></span>
                      Crypto Baskets Fees
                    </h5>
                    <div className="space-y-2 text-sm text-gray-700 ml-4">
                      <div className="flex justify-between">
                        <span>Performance Fee:</span>
                        <span className="font-medium">20%</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Management Fee:</span>
                        <span className="font-medium">2%</span>
                      </div>
                    </div>
                  </div>

                  <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                    <h5 className="font-semibold text-gray-900 mb-3 flex items-center">
                      <span className="w-2 h-2 bg-[#676DFF] rounded-full mr-2"></span>
                      Crypto Vaults Fees
                    </h5>
                    <div className="space-y-2 text-sm text-gray-700 ml-4">
                      <div className="flex justify-between">
                        <span>Performance Fee:</span>
                        <span className="font-medium">20%</span>
                      </div>
                    </div>
                  </div>

                  <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                    <h5 className="font-semibold text-gray-900 mb-3 flex items-center">
                      <span className="w-2 h-2 bg-[#676DFF] rounded-full mr-2"></span>
                      Crypto SIP Fees
                    </h5>
                    <div className="space-y-2 text-sm text-gray-700 ml-4">
                      <div className="flex justify-between">
                        <span>Management Fee:</span>
                        <span className="font-medium">1%</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Fee per Swap:</span>
                        <span className="font-medium">0.5%</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Creation Fee (one-time):</span>
                        <span className="font-medium">$1</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <p className="text-xs text-blue-800">
                    <strong>Fee Disclosure:</strong> All fees are clearly disclosed and will be deducted from your account as specified. Performance fees are charged only on realized gains above the high-water mark. Management fees are calculated on assets under management and charged periodically.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Signature Section */}
          <div className="border-t pt-6 mt-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-semibold text-gray-900 mb-3">Client Acknowledgment</h4>
                <div className="space-y-2 text-sm">
                  <p><strong>Name:</strong> {formDataForTerms.fullName}</p>
                  <p><strong>Date:</strong> {formDataForTerms.effectiveDate}</p>
                  {getSignatureData() && (
                    <div>
                      <p><strong>Electronic Signature:</strong></p>
                      <img 
                        src={getSignatureData()} 
                        alt="Client Signature" 
                        className="border border-gray-300 rounded p-2 bg-white max-w-full h-20 object-contain"
                      />
                    </div>
                  )}
                </div>
              </div>
              
              <div>
                <h4 className="font-semibold text-gray-900 mb-3">Company Information</h4>
                <div className="text-sm text-gray-700 space-y-1">
                  <p><strong>Company:</strong> Investment Advisory Services</p>
                  <p><strong>Date:</strong> {formDataForTerms.effectiveDate}</p>
                  <p><strong>Authorized Representative:</strong> _________________</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white font-['Lexend']">
      {/* FIXED: Success Message Overlay */}
      {showSuccessMessage && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-[#151515] border border-green-500/30 rounded-xl p-8 max-w-md w-full text-center shadow-2xl">
            <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="h-8 w-8 text-green-500" />
            </div>
            <h3 className="text-xl font-semibold text-white mb-2">KYC Submitted Successfully!</h3>
            <p className="text-[#B8B8B8] text-sm mb-4">
              Your documents have been submitted for review. Our team will process your application within 1-3 business days.
            </p>
            <div className="w-full bg-[#505050]/20 rounded-full h-2">
              <div className="bg-green-500 h-2 rounded-full animate-pulse w-full"></div>
            </div>
            <p className="text-xs text-[#B8B8B8] mt-2">Redirecting you to profile...</p>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="border-b border-[#151515] bg-[#0A0A0A]/95 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-6">
          <div className="flex items-center justify-between">
            <div>

              <p className="text-[#B8B8B8] text-xs sm:text-sm">Manage your account information and complete KYC verification.</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-8">
        {/* Tab Navigation */}
        <div className="mb-6 sm:mb-8">
          <div className="border-b border-[#151515]">
            {/* Desktop Navigation */}
            <nav className="hidden md:flex -mb-px space-x-8">
              <button
                onClick={() => setActiveTab('profile')}
                className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors duration-200 ${
                  activeTab === 'profile'
                    ? 'border-[#676DFF] text-[#676DFF]'
                    : 'border-transparent text-[#B8B8B8] hover:text-white hover:border-[#505050]'
                }`}
              >
                Profile Information
              </button>
              <button
                onClick={() => setActiveTab('kyc')}
                className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors duration-200 ${
                  activeTab === 'kyc'
                    ? 'border-[#676DFF] text-[#676DFF]'
                    : 'border-transparent text-[#B8B8B8] hover:text-white hover:border-[#505050]'
                } flex items-center`}
              >
                KYC Verification
                {getKYCStatus() === 'not_submitted' && (
                  <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-[#676DFF] text-white">
                    Required
                  </span>
                )}
              </button>
            </nav>

            {/* Mobile Navigation */}
            <div className="md:hidden">
              <div className="flex space-x-4 pb-3">
                <button
                  onClick={() => setActiveTab('profile')}
                  className={`flex-1 py-2 px-3 text-center rounded-md text-sm font-medium transition-colors duration-200 ${
                    activeTab === 'profile'
                      ? 'bg-[#676DFF] text-white'
                      : 'bg-[#151515] text-[#B8B8B8] hover:text-white'
                  }`}
                >
                  Profile
                </button>
                <button
                  onClick={() => setActiveTab('kyc')}
                  className={`flex-1 py-2 px-3 text-center rounded-md text-sm font-medium transition-colors duration-200 relative ${
                    activeTab === 'kyc'
                      ? 'bg-[#676DFF] text-white'
                      : 'bg-[#151515] text-[#B8B8B8] hover:text-white'
                  }`}
                >
                  KYC
                  {getKYCStatus() === 'not_submitted' && (
                    <span className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full"></span>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Tab Content */}
        {activeTab === 'profile' ? (
          <div className="space-y-6 sm:space-y-8">
            {/* Profile Information Card */}
            <div className="bg-[#151515] border border-[#505050]/20 rounded-lg p-4 sm:p-6">
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-4 sm:mb-6 space-y-3 sm:space-y-0">
                <div className="flex items-center space-x-3">
                  <User className="h-5 w-5 text-[#676DFF]" />
                  <h2 className="text-lg font-semibold">Profile Information</h2>
                </div>
                {/* {!isEditing && kycStatusInfo.canEdit && (
                  <button
                    onClick={() => setIsEditing(true)}
                    className="flex items-center justify-center space-x-2 px-4 py-2 bg-[#676DFF]/10 hover:bg-[#676DFF]/20 border border-[#676DFF]/20 rounded-md text-[#676DFF] text-sm transition-colors duration-200 w-full sm:w-auto"
                  >
                    <Edit2 className="h-4 w-4" />
                    <span>Edit Profile</span>
                  </button>
                )} */}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                {/* First Name */}
                <div>
                  <label className="block text-sm font-medium text-[#B8B8B8] mb-2">First Name</label>
                  {isEditing ? (
                    <input
                      type="text"
                      value={profileData.firstName}
                      onChange={(e) => setProfileData(prev => ({ ...prev, firstName: e.target.value }))}
                      className="w-full p-3 bg-[#0A0A0A] border border-[#505050] rounded-md focus:ring-2 focus:ring-[#676DFF] focus:border-[#676DFF] text-white placeholder-[#808080] transition-colors duration-200"
                      placeholder="Enter your first name"
                    />
                  ) : (
                    <div className="p-3 bg-[#0A0A0A] border border-[#505050]/20 rounded-md text-white">
                      {profileData.firstName || 'Not provided'}
                    </div>
                  )}
                  {errors.firstName && <p className="text-red-400 text-sm mt-1">{errors.firstName}</p>}
                </div>

                {/* Last Name */}
                <div>
                  <label className="block text-sm font-medium text-[#B8B8B8] mb-2">Last Name</label>
                  {isEditing ? (
                    <input
                      type="text"
                      value={profileData.lastName}
                      onChange={(e) => setProfileData(prev => ({ ...prev, lastName: e.target.value }))}
                      className="w-full p-3 bg-[#0A0A0A] border border-[#505050] rounded-md focus:ring-2 focus:ring-[#676DFF] focus:border-[#676DFF] text-white placeholder-[#808080] transition-colors duration-200"
                      placeholder="Enter your last name"
                    />
                  ) : (
                    <div className="p-3 bg-[#0A0A0A] border border-[#505050]/20 rounded-md text-white">
                      {profileData.lastName || 'Not provided'}
                    </div>
                  )}
                  {errors.lastName && <p className="text-red-400 text-sm mt-1">{errors.lastName}</p>}
                </div>

                {/* Email */}
                <div>
                  <label className="block text-sm font-medium text-[#B8B8B8] mb-2">Email ID</label>
                  <div className="p-3 bg-[#0A0A0A] border border-[#505050]/20 rounded-md text-white break-all">
                    {profileData.email || 'Not provided'}
                  </div>
                  <p className="text-xs text-[#B8B8B8] mt-1">Pre-filled from your account. You can edit if needed.</p>
                </div>

                {/* Phone */}
                <div>
                  <label className="block text-sm font-medium text-[#B8B8B8] mb-2">Phone Number</label>
                  {isEditing ? (
                    <input
                      type="tel"
                      value={profileData.phone}
                      onChange={(e) => setProfileData(prev => ({ ...prev, phone: e.target.value }))}
                      className="w-full p-3 bg-[#0A0A0A] border border-[#505050] rounded-md focus:ring-2 focus:ring-[#676DFF] focus:border-[#676DFF] text-white placeholder-[#808080] transition-colors duration-200"
                      placeholder="Enter your phone number"
                    />
                  ) : (
                    <div className="p-3 bg-[#0A0A0A] border border-[#505050]/20 rounded-md text-white flex items-center">
                      <Phone className="h-4 w-4 mr-2 text-[#808080] flex-shrink-0" />
                      <span className="break-all">{profileData.phone || 'Not provided'}</span>
                    </div>
                  )}
                  {errors.phone && <p className="text-red-400 text-sm mt-1">{errors.phone}</p>}
                </div>

                {/* Address - Full Width */}
                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-[#B8B8B8] mb-2">Residential Address</label>
                  {isEditing ? (
                    <textarea
                      value={profileData.address}
                      onChange={(e) => setProfileData(prev => ({ ...prev, address: e.target.value }))}
                      rows={3}
                      className="w-full p-3 bg-[#0A0A0A] border border-[#505050] rounded-md focus:ring-2 focus:ring-[#676DFF] focus:border-[#676DFF] text-white placeholder-[#808080] transition-colors duration-200 resize-none"
                      placeholder="Enter your residential address"
                    />
                  ) : (
                    <div className="p-3 bg-[#0A0A0A] border border-[#505050]/20 rounded-md text-white flex items-start">
                      <MapPin className="h-4 w-4 mr-2 text-[#808080] mt-0.5 flex-shrink-0" />
                      <span className="break-words">{profileData.address || 'Not provided'}</span>
                    </div>
                  )}
                  {errors.address && <p className="text-red-400 text-sm mt-1">{errors.address}</p>}
                </div>
              </div>
            </div>

     {/* ⭐ ADD THIS NEW WALLET ADDRESS SECTION HERE ⭐ */}
  <div className="sm:col-span-2">
    <label className="block text-lg font-semibold text-white mb-3 flex items-center gap-2">
      <Wallet className="h-5 w-5 text-[#676DFF]" />
      Wallet Address
    </label>
    <div className="p-5 rounded-xl shadow-lg" style={{ background: 'var(--bg-surface)', border: `1px solid var(--card-border)` }}>
      <div className="mb-3">
        <p className="text-xs text-[#B8B8B8] mb-2">Your unique USDT wallet address (Polygon Network)</p>
        <code className="text-[#676DFF] text-lg md:text-xl font-mono break-all block p-4 rounded-lg border leading-relaxed" style={{ background: 'var(--bg-base)', borderColor: 'var(--border-subtle)', color: 'var(--text-primary)' }}>
          {client?.wallet_address || userStatus?.userData?.wallet_address || 'Not generated yet'}
        </code>
      </div>
      <div className="flex items-center gap-3 pt-3 border-t" style={{ borderColor: 'var(--border-subtle)' }}>
        <button
          onClick={() => {
            const address = client?.wallet_address || userStatus?.userData?.wallet_address;
            if (address) {
              navigator.clipboard.writeText(address);
            }
          }}
          className="flex items-center gap-2 px-4 py-2 bg-[#676DFF] hover:bg-[#5A60E8] rounded-lg text-white text-sm font-medium transition-colors shadow-md"
        >
          <Copy className="h-4 w-4" />
          Copy Address
        </button>
        <div className="flex items-center gap-2 text-xs text-[#B8B8B8]">
          <CheckCircle className="h-3 w-3 text-green-500" />
          <span>Use this address to receive USDT</span>
        </div>
      </div>
    </div>
  </div>

            {/* Account Status Card */}
            <div className="bg-[#151515] border border-[#505050]/20 rounded-lg p-4 sm:p-6">
              <h2 className="text-lg font-semibold mb-4 sm:mb-6">Account Status</h2>
              
              <div className={`${kycStatusInfo.bgColor} border ${kycStatusInfo.borderColor} rounded-lg p-4 mb-4 sm:mb-6 backdrop-blur-sm`}>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
                  <div className="flex items-start sm:items-center space-x-3">
                    <div className="p-2 rounded-full bg-[#676DFF]/10 flex-shrink-0">
                      <kycStatusInfo.icon className={`h-4 w-4 ${kycStatusInfo.color}`} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-white">
                        {getKYCStatus() === 'verified' ? 'KYC Verification Complete' : 
                         getKYCStatus() === 'submitted' || getKYCStatus() === 'pending' ? 'KYC Under Review' : 
                         getKYCStatus() === 'rejected' ? 'KYC Application Rejected' :
                         'KYC Verification Required'}
                      </p>
                      <p className={`text-sm ${kycStatusInfo.color} break-words`}>{kycStatusInfo.title}</p>
                    </div>
                  </div>
                  {getKYCStatus() === 'verified' && (
                    <button className="flex items-center justify-center space-x-2 px-4 py-2 bg-green-500/10 border border-green-500/20 rounded-md text-green-500 text-sm hover:bg-green-500/20 transition-colors duration-200 w-full sm:w-auto">
                      <Download className="h-4 w-4" />
                      <span>Download Certificate</span>
                    </button>
                  )}
                  {getKYCStatus() === 'rejected' && (
                    <button 
                      onClick={() => setShowRejectionModal(true)}
                      className="flex items-center justify-center space-x-2 px-4 py-2 bg-red-500/10 border border-red-500/20 rounded-md text-red-500 text-sm hover:bg-red-500/20 transition-colors duration-200 w-full sm:w-auto"
                    >
                      <AlertCircle className="h-4 w-4" />
                      <span>View Rejection Reason</span>
                    </button>
                  )}
                </div>
              </div>
              
              <div className="grid grid-cols-1 gap-4 sm:gap-6 text-sm">
                <div>
                  <p className="text-[#B8B8B8] mb-1">Client ID</p>
                  <p className="font-medium text-white break-all">
                    {getActualClientId() || 'Pending'}
                  </p>
                </div>
              </div>
            </div>
          </div>) : (
          // KYC Tab Content
<div className="space-y-6 sm:space-y-8">
  {getKYCStatus() === 'verified' ? (
    <div className="bg-[#151515] border border-[#505050]/20 rounded-lg p-6 sm:p-8 text-center">
      <CheckCircle className="h-12 w-12 sm:h-16 sm:w-16 text-green-500 mx-auto mb-4" />
      <h3 className="text-lg sm:text-xl font-semibold mb-2 text-white">KYC Verification Complete</h3>
      <p className="text-[#B8B8B8] mb-6 text-sm sm:text-base">
        Your identity has been successfully verified. You can now access all investment features.
      </p>
      <button 
        onClick={handleDownloadPdf}
        className="flex items-center justify-center space-x-2 mx-auto px-6 py-3 bg-green-500/10 border border-green-500/20 rounded-md text-green-500 hover:bg-green-500/20 transition-colors duration-200 w-full sm:w-auto"
      >
        <Download className="h-4 w-4" />
        <span>Download Verification Certificate</span>
      </button>
    </div>
  ) : getKYCStatus() === 'submitted' || getKYCStatus() === 'pending' ? (
    <div className="bg-[#151515] border border-[#505050]/20 rounded-lg p-6 sm:p-8 text-center">
      <Clock className="h-12 w-12 sm:h-16 sm:w-16 text-[#676DFF] mx-auto mb-4" />
      <h3 className="text-lg sm:text-xl font-semibold mb-2 text-white">KYC Under Review</h3>
      <p className="text-[#B8B8B8] mb-6 text-sm sm:text-base">
        Thank you for submitting your documents. Our team is reviewing your application and will notify you once the verification is complete.
      </p>
      <div className="bg-[#676DFF]/10 border border-[#676DFF]/20 p-4 rounded-lg text-sm text-[#676DFF] backdrop-blur-sm">
        <p><strong>Processing Time:</strong> 1-3 business days</p>
        <p><strong>Status:</strong> Documents received and under review</p>
      </div>
    </div>
  ) : (
    // FIXED: Show KYC form for both not_submitted AND rejected status
    <>
      {/* Show rejection notice only for rejected status */}
      {getKYCStatus() === 'rejected' && (
        <div className="bg-[#151515] border border-red-500/20 rounded-lg p-6 sm:p-8 mb-6">
          <div className="text-center mb-6">
            <AlertCircle className="h-12 w-12 sm:h-16 sm:w-16 text-red-500 mx-auto mb-4" />
            <h3 className="text-lg sm:text-xl font-semibold mb-2 text-white">KYC Application Rejected</h3>
            <p className="text-[#B8B8B8] text-sm sm:text-base mb-4">
              Your KYC application has been rejected. Please review the feedback and resubmit with corrections.
            </p>
            <button 
              onClick={() => setShowRejectionModal(true)}
              className="flex items-center justify-center space-x-2 mx-auto px-6 py-3 bg-red-500/10 border border-red-500/20 rounded-md text-red-500 hover:bg-red-500/20 transition-colors duration-200 w-full sm:w-auto mb-6"
            >
              <AlertCircle className="h-4 w-4" />
              <span>View Rejection Reason</span>
            </button>
          </div>
          
          <div className="border-t border-red-500/20 pt-6">
            <h4 className="text-lg font-semibold mb-4 text-white">Resubmit KYC Application</h4>
            <p className="text-[#B8B8B8] text-sm mb-6">
              Please address the concerns mentioned in the rejection reason and complete the form below to resubmit.
            </p>
                </div>
              </div>
      )}
            
              
              
                {/* KYC Requirements */}
                <div className="bg-[#151515] border border-[#505050]/20 rounded-lg p-4 sm:p-6">
                  <h2 className="text-lg font-semibold mb-4 sm:mb-6 text-white">KYC Verification Requirements</h2>
                  
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8">
                    <div className="space-y-4">
                      <h4 className="font-medium text-white">Required Documents:</h4>
                      <div className="space-y-4">
                        <div className="flex items-start space-x-3">
                          <FileText className="h-5 w-5 text-[#676DFF] mt-0.5 flex-shrink-0" />
                          <div className="min-w-0 flex-1">
                            <p className="font-medium text-white">Government-issued ID</p>
                            <p className="text-sm text-[#B8B8B8]">Passport, driver's license, or national ID card</p>
                          </div>
                        </div>
                        <div className="flex items-start space-x-3">
                          <FileText className="h-5 w-5 text-[#676DFF] mt-0.5 flex-shrink-0" />
                          <div className="min-w-0 flex-1">
                            <p className="font-medium text-white">Proof of Address</p>
                            <p className="text-sm text-[#B8B8B8]">Utility bill, bank statement, or lease agreement (within 3 months)</p>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="space-y-4">
                      <h4 className="font-medium text-white">Verification Process:</h4>
                      <div className="space-y-3 text-sm">
                        <div className="flex items-center space-x-3">
                          <div className="w-6 h-6 bg-[#676DFF] text-white rounded-full flex items-center justify-center text-xs font-medium flex-shrink-0">1</div>
                          <span className="text-white">Complete profile information</span>
                        </div>
                        <div className="flex items-center space-x-3">
                          <div className="w-6 h-6 bg-[#676DFF] text-white rounded-full flex items-center justify-center text-xs font-medium flex-shrink-0">2</div>
                          <span className="text-white">Upload required documents</span>
                        </div>
                        <div className="flex items-center space-x-3">
                          <div className="w-6 h-6 bg-[#676DFF] text-white rounded-full flex items-center justify-center text-xs font-medium flex-shrink-0">3</div>
                          <span className="text-white">Submit for review</span>
                        </div>
                        <div className="flex items-center space-x-3">
                          <div className="w-6 h-6 bg-[#505050] text-white rounded-full flex items-center justify-center text-xs font-medium flex-shrink-0">4</div>
                          <span className="text-[#B8B8B8]">Verification complete (1-3 days)</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* KYC & Agreement Form */}
      <div className="bg-[#151515] border border-[#505050]/20 rounded-lg p-4 sm:p-6">
        <h2 className="text-lg font-semibold mb-4 sm:mb-6 text-white">
          {getKYCStatus() === 'rejected' ? 'Resubmit KYC & Investor Advisory Agreement' : 'KYC & Investor Advisory Agreement'}
        </h2>
        <p className="text-[#B8B8B8] text-sm mb-6 sm:mb-8">
          {getKYCStatus() === 'rejected' 
            ? 'Please address the feedback and complete the form below to resubmit your application.'
            : 'To get started investing, please complete your KYC and sign our Advisory Agreement.'
          }
        </p>
                  
                  <div className="space-y-4 sm:space-y-6">
                    {/* Full Name - FIXED */}
                    <div>
                      <label className="block text-sm font-medium text-[#B8B8B8] mb-2">Full Name *</label>
                      <input
                        type="text"
                        value={fullNameInput}
                        onChange={(e) => {
                          setFullNameInput(e.target.value);
                          // Split the name and update profileData in real-time for validation
                          const names = e.target.value.trim().split(/\s+/);
                          setProfileData(prev => ({
                            ...prev,
                            firstName: names[0] || '',
                            lastName: names.slice(1).join(' ') || ''
                          }));
                        }}
                        className="w-full p-3 bg-[#0A0A0A] border border-[#505050] rounded-md focus:ring-2 focus:ring-[#676DFF] focus:border-[#676DFF] text-white placeholder-[#808080] transition-colors duration-200"
                        placeholder="Enter your full name"
                        required
                      />
                      <p className="text-xs text-[#B8B8B8] mt-1">Pre-filled from your account. You can edit if needed.</p>
                      {errors.firstName && <p className="text-red-400 text-sm mt-1">{errors.firstName}</p>}
                    </div>

                    {/* Email ID */}
                    <div>
                      <label className="block text-sm font-medium text-[#B8B8B8] mb-2">Email ID *</label>
                      <input
                        type="email"
                        value={profileData.email}
                        onChange={(e) => setProfileData(prev => ({ ...prev, email: e.target.value }))}
                        className="w-full p-3 bg-[#0A0A0A] border border-[#505050] rounded-md focus:ring-2 focus:ring-[#676DFF] focus:border-[#676DFF] text-white placeholder-[#808080] transition-colors duration-200"
                        placeholder="Enter your email address"
                        required
                      />
                      <p className="text-xs text-[#B8B8B8] mt-1">Pre-filled from your account. You can edit if needed.</p>
                      {errors.email && <p className="text-red-400 text-sm mt-1">{errors.email}</p>}
                    </div>

                    {/* Phone Number with CustomPhoneInput */}
                    <div>
                      <label className="block text-sm font-medium text-[#B8B8B8] mb-2">Phone Number *</label>
                      <div className="country-phone-input-container">
                        <CustomPhoneInput
                          value={profileData.phone}
                          onChange={(fullNumber, countryCode) => {
                            setProfileData(prev => ({
                              ...prev,
                              phone: fullNumber.replace(countryCode, '')
                            }));
                            setKycData(prev => ({
                              ...prev,
                              countryCode: countryCode
                            }));
                          }}
                          placeholder="Enter phone number"
                          defaultCountry="+91"
                        />
                      </div>
                      {errors.phone && <p className="text-red-400 text-sm mt-1">{errors.phone}</p>}
                    </div>

                    {/* Residential Address */}
                    <div>
                      <label className="block text-sm font-medium text-[#B8B8B8] mb-2">Residential Address *</label>
                      <textarea
                        value={profileData.address}
                        onChange={(e) => setProfileData(prev => ({ ...prev, address: e.target.value }))}
                        rows={3}
                        className="w-full p-3 bg-[#0A0A0A] border border-[#505050] rounded-md focus:ring-2 focus:ring-[#676DFF] focus:border-[#676DFF] text-white placeholder-[#808080] transition-colors duration-200 resize-none"
                        placeholder="Enter your residential address"
                        required
                      />
                      {errors.address && <p className="text-red-400 text-sm mt-1">{errors.address}</p>}
                    </div>

                    {/* KYC Document Type
                    <div>
                      <label className="block text-sm font-medium text-[#B8B8B8] mb-2">KYC Document Type *</label>
                      <select
                        value={kycData.kycType}
                        onChange={(e) => setKycData(prev => ({ ...prev, kycType: e.target.value, customKycType: '' }))}
                        className="w-full p-3 bg-[#0A0A0A] border border-[#505050] rounded-md focus:ring-2 focus:ring-[#676DFF] focus:border-[#676DFF] text-white transition-colors duration-200"
                        required
                      >
                        <option value="">--Select--</option>
                        <option value="Aadhaar card">Aadhaar card</option>
                        <option value="PAN Card">PAN Card</option>
                        <option value="Driving License">Driving License</option>
                        <option value="Passport">Passport</option>
                        <option value="Others">Others</option>
                      </select>
                      
                      {kycData.kycType === 'Others' && (
                        <input
                          type="text"
                          placeholder="Specify KYC Type"
                          value={kycData.customKycType}
                          onChange={(e) => setKycData(prev => ({ ...prev, customKycType: e.target.value }))}
                          className="w-full p-3 bg-[#0A0A0A] border border-[#505050] rounded-md focus:ring-2 focus:ring-[#676DFF] focus:border-[#676DFF] text-white placeholder-[#808080] transition-colors duration-200 mt-3"
                          required
                        />
                      )}
                      {errors.kycType && <p className="text-red-400 text-sm mt-1">{errors.kycType}</p>}
                      {errors.customKycType && <p className="text-red-400 text-sm mt-1">{errors.customKycType}</p>}
                    </div> */}

                    {/* Address Proof Documents */}
                    <div>
                      <label className="block text-sm font-medium text-[#B8B8B8] mb-2">Address Proof Documents *</label>
                      <p className="text-xs text-[#B8B8B8] mb-3">Upload utility bill, bank statement, or lease agreement (within 3 months). Maximum 2 documents.</p>
                      
                      <div className="border-2 border-dashed border-[#505050] rounded-lg p-4 sm:p-6 text-center hover:border-[#676DFF]/50 transition-colors duration-200">
                        <Upload className="h-6 w-6 sm:h-8 sm:w-8 text-[#808080] mx-auto mb-2" />
                        <p className="text-sm text-[#B8B8B8] mb-2">
                          Click to upload address proof documents (PDF, JPG, PNG)
                        </p>
                        <input
                          type="file"
                          accept=".jpg,.jpeg,.png,.pdf"
                          onChange={(e) => handleFileUpload('addressProofDocuments', e.target.files[0])}
                          className="hidden"
                          id="address-proof-upload"
                        />
                        <label htmlFor="address-proof-upload">
                          <button
                            type="button"
                            className="px-4 sm:px-6 py-2 bg-[#676DFF] hover:bg-[#676DFF]/90 text-white rounded-md text-sm font-medium transition-colors duration-200 cursor-pointer w-full sm:w-auto"
                            onClick={() => document.getElementById('address-proof-upload')?.click()}
                          >
                            Upload Address Proof
                          </button>
                        </label>
                        
                        {/* Display uploaded address proof documents */}
                        {kycData.addressProofDocuments && kycData.addressProofDocuments.length > 0 && (
                          <div className="mt-4 space-y-2">
                            {kycData.addressProofDocuments.map((file, index) => (
                              <div key={index} className="flex items-center justify-between bg-[#0A0A0A] p-2 rounded border border-[#505050]">
                                <span className="text-sm text-white break-all flex-1">{file.name}</span>
                                <button
                                  onClick={() => removeFile('addressProofDocuments', index)}
                                  className="text-red-400 hover:text-red-300 text-sm ml-2 flex-shrink-0"
                                >
                                  <X className="h-4 w-4" />
                                </button>
                              </div>
                            ))}
                            <p className="text-xs text-[#B8B8B8]">
                              {kycData.addressProofDocuments.length}/2 documents uploaded
                            </p>
                          </div>
                        )}
                      </div>
                      {errors.addressProofDocuments && <p className="text-red-400 text-sm mt-1">{errors.addressProofDocuments}</p>}
                    </div>

                    {/* ID Proof Documents */}
                    <div>
                      <label className="block text-sm font-medium text-[#B8B8B8] mb-2">ID Proof Documents *</label>
                      <p className="text-xs text-[#B8B8B8] mb-3">Upload government-issued ID (front and back if applicable). Maximum 2 documents.</p>
                      
                      <div className="border-2 border-dashed border-[#505050] rounded-lg p-4 sm:p-6 text-center hover:border-[#676DFF]/50 transition-colors duration-200">
                        <Upload className="h-6 w-6 sm:h-8 sm:w-8 text-[#808080] mx-auto mb-2" />
                        <p className="text-sm text-[#B8B8B8] mb-2">
                          Click to upload ID proof documents (PDF, JPG, PNG)
                        </p>
                        <input
                          type="file"
                          accept=".jpg,.jpeg,.png,.pdf"
                          onChange={(e) => handleFileUpload('idProofDocuments', e.target.files[0])}
                          className="hidden"
                          id="id-proof-upload"
                        />
                        <label htmlFor="id-proof-upload">
                          <button
                            type="button"
                            className="px-4 sm:px-6 py-2 bg-[#676DFF] hover:bg-[#676DFF]/90 text-white rounded-md text-sm font-medium transition-colors duration-200 cursor-pointer w-full sm:w-auto"
                            onClick={() => document.getElementById('id-proof-upload')?.click()}
                          >
                            Upload ID Proof
                          </button>
                        </label>
                        
                        {/* Display uploaded ID proof documents */}
                        {kycData.idProofDocuments && kycData.idProofDocuments.length > 0 && (
                          <div className="mt-4 space-y-2">
                            {kycData.idProofDocuments.map((file, index) => (
                              <div key={index} className="flex items-center justify-between bg-[#0A0A0A] p-2 rounded border border-[#505050]">
                                <span className="text-sm text-white break-all flex-1">{file.name}</span>
                                <button
                                  onClick={() => removeFile('idProofDocuments', index)}
                                  className="text-red-400 hover:text-red-300 text-sm ml-2 flex-shrink-0"
                                >
                                  <X className="h-4 w-4" />
                                </button>
                              </div>
                            ))}
                            <p className="text-xs text-[#B8B8B8]">
                              {kycData.idProofDocuments.length}/2 documents uploaded
                            </p>
                          </div>
                        )}
                      </div>
                      {errors.idProofDocuments && <p className="text-red-400 text-sm mt-1">{errors.idProofDocuments}</p>}
                    </div>

                    {/* Signature Canvas */}
                    <div>
                      <h3 className="text-base sm:text-lg font-medium text-white mb-4">Digital Signature *</h3>
                      <div className="bg-white border border-[#505050] rounded-lg p-2 sm:p-4">
                        <canvas
                          ref={signatureCanvasRef}
                          className="w-full border border-gray-300 rounded cursor-crosshair touch-none"
                          onMouseDown={startDrawing}
                          onMouseMove={draw}
                          onMouseUp={stopDrawing}
                          onMouseLeave={stopDrawing}
                          onTouchStart={handleTouchStart}
                          onTouchMove={handleTouchMove}
                          onTouchEnd={handleTouchEnd}
                          style={{ width: '100%', height: '150px' }}
                        />
                      </div>
                      <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3 mt-3">
                        <button
                          type="button"
                          onClick={clearSignature}
                          className="px-4 py-2 bg-[#505050] hover:bg-[#505050]/80 text-white rounded-md text-sm transition-colors duration-200 w-full sm:w-auto"
                        >
                          Clear Signature
                        </button>
                        <p className="text-xs text-[#B8B8B8] sm:mt-2">Draw your signature in the white box above</p>
                      </div>
                      {errors.signature && <p className="text-red-400 text-sm mt-1">{errors.signature}</p>}
                    </div>

                    {/* Upload Signature */}
                    <div>
                      <label className="block text-sm font-medium text-[#B8B8B8] mb-2">Upload Signature (Alternative)</label>
                      <div className="border-2 border-dashed border-[#505050] rounded-lg p-4 sm:p-6 text-center hover:border-[#676DFF]/50 transition-colors duration-200">
                        <Upload className="h-6 w-6 sm:h-8 sm:w-8 text-[#808080] mx-auto mb-2" />
                        <p className="text-sm text-[#B8B8B8] mb-2 break-words">
                          {kycData.uploadedSignature ? 
                            `Selected: ${kycData.uploadedSignature.name}` : 
                            'Click to upload your signature (JPG or PNG)'
                          }
                        </p>
                        <input
                          type="file"
                          accept=".jpg,.jpeg,.png"
                          onChange={(e) => handleSignatureUpload(e.target.files[0])}
                          className="hidden"
                          id="signature-upload"
                          ref={signatureInputRef}
                        />
                        <label htmlFor="signature-upload">
                          <button
                            type="button"
                            className="px-4 sm:px-6 py-2 bg-[#676DFF] hover:bg-[#676DFF]/90 text-white rounded-md text-sm font-medium transition-colors duration-200 cursor-pointer w-full sm:w-auto"
                            onClick={() => signatureInputRef.current?.click()}
                          >
                            Upload Signature
                          </button>
                        </label>
                        {kycData.uploadedSignature && (
                          <div className="mt-3 flex items-center justify-center space-x-2">
                            <span className="text-sm text-white break-all">{kycData.uploadedSignature.name}</span>
                            <button
                              onClick={removeSignature}
                              className="text-red-400 hover:text-red-300 text-sm flex-shrink-0"
                            >
                              <X className="h-4 w-4" />
                            </button>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Terms and Conditions */}
                    <div className="space-y-4 pt-4 sm:pt-6 border-t border-[#505050]/20">
                      <label className="flex items-start space-x-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={kycData.termsAccepted}
                          onChange={(e) => setKycData(prev => ({ ...prev, termsAccepted: e.target.checked }))}
                          className="w-4 h-4 text-[#676DFF] bg-[#0A0A0A] border-[#505050] rounded focus:ring-[#676DFF] focus:ring-2 mt-0.5 flex-shrink-0"
                        />
                        <span className="text-sm text-white">I have read and accept the terms and conditions</span>
                      </label>
                      <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3">
                        <button
                          type="button"
                          onClick={() => setShowTermsModal(true)}
                          className="px-4 py-2 bg-[#505050]/20 border border-[#505050] hover:bg-[#505050]/30 text-[#B8B8B8] rounded-md text-sm transition-colors duration-200"
                        >
                          View Agreement
                        </button>
                        <button
                          type="button"
                          onClick={handleDownloadPdf}
                          className="px-4 py-2 bg-[#505050]/20 border border-[#505050] hover:bg-[#505050]/30 text-[#B8B8B8] rounded-md text-sm transition-colors duration-200"
                        >
                          Download PDF
                        </button>
                      </div>
                      {errors.terms && <p className="text-red-400 text-sm mt-1">{errors.terms}</p>}
                    </div>

                    {/* Submit Button */}
        <div className="pt-4">
          <button
            onClick={handleKYCSubmit}
            disabled={!kycData.termsAccepted || isSubmitting}
            className="w-full py-3 bg-[#676DFF] hover:bg-[#676DFF]/90 disabled:bg-[#505050] disabled:cursor-not-allowed text-white rounded-md font-medium transition-colors duration-200"
          >
            {isSubmitting ? 
              (getKYCStatus() === 'rejected' ? 'Resubmitting Agreement...' : 'Submitting Agreement...') : 
              (getKYCStatus() === 'rejected' ? 'Resubmit Agreement' : 'Submit Agreement')
            }
          </button>
          {errors.submit && <p className="text-red-400 text-sm mt-2">{errors.submit}</p>}
        </div>
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/* FIXED: Professional Terms Modal */}
        {showTermsModal && (
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
              <div className="fixed inset-0 transition-opacity bg-black bg-opacity-75 backdrop-blur-sm" onClick={() => setShowTermsModal(false)}></div>
              
              <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>
              
              <div className="inline-block align-bottom bg-white rounded-xl text-left overflow-hidden shadow-2xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full mx-4 border border-[#E0E0E0]">
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <div className="flex items-center justify-between mb-4 pb-4 border-b border-[#E0E0E0]">
                    <h3 className="text-xl font-semibold text-gray-900 font-['Lexend']">Investor Advisory Agreement</h3>
                    <button
                      onClick={() => setShowTermsModal(false)}
                      className="text-gray-400 hover:text-gray-600 transition-colors duration-200 p-2 rounded-full hover:bg-gray-100"
                    >
                      <X className="h-5 w-5" />
                    </button>
                  </div>
                  
                  <div className="max-h-96 overflow-y-auto pr-2 custom-scrollbar">
                    <TermsModalContent />
                  </div>
                </div>
                
                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse border-t border-[#E0E0E0]">
                  <button
                    onClick={() => {
                      setKycData(prev => ({ ...prev, termsAccepted: true }));
                      setShowTermsModal(false);
                    }}
                    className="w-full inline-flex justify-center rounded-lg border border-transparent shadow-sm px-6 py-3 bg-[#676DFF] text-base font-medium text-white hover:bg-[#676DFF]/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#676DFF] sm:ml-3 sm:w-auto sm:text-sm transition-colors duration-200 font-['Lexend']"
                  >
                    Accept & Continue
                  </button>
                  <button
                    onClick={() => setShowTermsModal(false)}
                    className="mt-3 w-full inline-flex justify-center rounded-lg border border-[#B8B8B8] shadow-sm px-6 py-3 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#676DFF] sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm transition-colors duration-200 font-['Lexend']"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Rejection Reason Modal */}
        {showRejectionModal && (
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
              <div className="fixed inset-0 transition-opacity bg-black bg-opacity-75 backdrop-blur-sm" onClick={() => setShowRejectionModal(false)}></div>
              
              <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>
              
              <div className="inline-block align-bottom bg-[#151515] rounded-xl text-left overflow-hidden shadow-2xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full mx-4 border border-red-500/30">
                <div className="bg-[#151515] px-6 pt-6 pb-4">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-red-500/20 rounded-full flex items-center justify-center">
                        <AlertCircle className="h-5 w-5 text-red-500" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-white font-['Lexend']">KYC Application Rejected</h3>
                        <p className="text-sm text-[#B8B8B8]">Please review the feedback below</p>
                      </div>
                    </div>
                    <button
                      onClick={() => setShowRejectionModal(false)}
                      className="text-[#B8B8B8] hover:text-white transition-colors duration-200 p-2 rounded-full hover:bg-[#505050]/20"
                    >
                      <X className="h-5 w-5" />
                    </button>
                  </div>
                  
                  <div className="mb-6">
                    <h4 className="text-sm font-medium text-[#B8B8B8] mb-3">Rejection Reason:</h4>
                    <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4">
                      <p className="text-sm text-white leading-relaxed">
                        {getRejectionComments() || 'No specific reason provided. Please contact support for more details.'}
                      </p>
                    </div>
                  </div>

                  <div className="bg-[#0A0A0A] border border-[#505050]/20 rounded-lg p-4 mb-6">
                    <h4 className="text-sm font-medium text-white mb-2">Next Steps:</h4>
                    <ul className="text-sm text-[#B8B8B8] space-y-1 list-disc list-inside">
                      <li>Review the rejection reason carefully</li>
                      <li>Prepare updated documents addressing the concerns</li>
                      <li>Resubmit your KYC application with corrected information</li>
                      <li>Contact support if you need clarification</li>
                    </ul>
                  </div>
                </div>
                
                <div className="bg-[#0A0A0A] px-6 py-4 border-t border-[#505050]/20 flex flex-col sm:flex-row-reverse space-y-3 sm:space-y-0 sm:space-x-3 sm:space-x-reverse">
                  <button
                    onClick={() => {
                      setShowRejectionModal(false);
                      setActiveTab('kyc');
                    }}
                    className="w-full inline-flex justify-center rounded-lg border border-transparent shadow-sm px-6 py-3 bg-[#676DFF] text-base font-medium text-white hover:bg-[#676DFF]/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#676DFF] sm:w-auto sm:text-sm transition-colors duration-200 font-['Lexend']"
                  >
                    Resubmit KYC
                  </button>
                  <button
                    onClick={() => setShowRejectionModal(false)}
                    className="w-full inline-flex justify-center rounded-lg border border-[#505050] shadow-sm px-6 py-3 bg-[#151515] text-base font-medium text-[#B8B8B8] hover:bg-[#151515]/80 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#505050] sm:w-auto sm:text-sm transition-colors duration-200 font-['Lexend']"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Bottom Padding for Mobile Navigation */}
      <div className="md:hidden h-20"></div>

      <style jsx>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 3px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #676DFF;
          border-radius: 3px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #5A60E6;
        }
      `}</style>
    </div>
  );
};

export default ProfileSettings;

// import React, { useState, useRef, useEffect } from 'react';
// import { User, Upload, CheckCircle, AlertCircle, Download, Phone, MapPin, Calendar, Globe, FileText, Edit2, Save, X, Clock, Menu } from 'lucide-react';

// // Import Sentry tracking functions
// import { trackKYCEvent, trackDashboardError } from '../sentry';

// // Import Supabase client (create this file if it doesn't exist)
// import { supabase } from '../lib/supabaseClient';

// // Import your existing components and utilities
// import { makeAgreementPdf } from '../utils/pdfUtils';
// import CustomPhoneInput from './CustomPhoneInput';
// import './TermsModal.css';

// const FILE_VALIDATION_CONFIG = {
//   profileImage: {
//     allowedTypes: ['image/jpeg', 'image/png'],
//     maxSize: 5 * 1024 * 1024, // 5MB
//     errorPrefix: 'Profile image'
//   },
//   documents: {
//     allowedTypes: ['application/pdf', 'image/jpeg', 'image/png'],
//     maxSize: 10 * 1024 * 1024, // 10MB
//     errorPrefix: 'Document'
//   },
//   signature: {
//     allowedTypes: ['image/png', 'image/jpeg'],
//     maxSize: 2 * 1024 * 1024, // 2MB
//     errorPrefix: 'Signature'
//   }
// };

// const validateFile = (file, validationType) => {
//   if (!file) {
//     return { isValid: false, error: 'No file selected' };
//   }

//   const config = FILE_VALIDATION_CONFIG[validationType];
//   if (!config) {
//     return { isValid: false, error: 'Invalid validation type' };
//   }

//   // Check file type
//   if (!config.allowedTypes.includes(file.type)) {
//     const allowedExtensions = config.allowedTypes
//       .map(type => {
//         if (type === 'application/pdf') return 'PDF';
//         if (type === 'image/jpeg') return 'JPG';
//         if (type === 'image/png') return 'PNG';
//         return type;
//       })
//       .join(', ');
    
//     return { 
//       isValid: false, 
//       error: `${config.errorPrefix} must be one of: ${allowedExtensions}` 
//     };
//   }

//   // Check file size
//   if (file.size > config.maxSize) {
//     const maxSizeMB = (config.maxSize / (1024 * 1024)).toFixed(1);
//     const fileSizeMB = (file.size / (1024 * 1024)).toFixed(1);
    
//     return { 
//       isValid: false, 
//       error: `${config.errorPrefix} size (${fileSizeMB}MB) exceeds maximum allowed size of ${maxSizeMB}MB` 
//     };
//   }

//   // Check if file is corrupted (basic check)
//   if (file.size === 0) {
//     return { 
//       isValid: false, 
//       error: `${config.errorPrefix} appears to be corrupted or empty` 
//     };
//   }

//   return { isValid: true, error: null };
// };

// const ProfileSettings = ({ client, userStatus, onStatusUpdate }) => {
//   const [activeTab, setActiveTab] = useState('profile');
//   const [isEditing, setIsEditing] = useState(false);
//   const [isSubmitting, setIsSubmitting] = useState(false);
//   const [showSuccessMessage, setShowSuccessMessage] = useState(false);
//   const [showTermsModal, setShowTermsModal] = useState(false);
//   const [showRejectionModal, setShowRejectionModal] = useState(false);

//   // Get user info from props or context
//   const currentUser = client || userStatus || {
//     name: 'User Name',
//     email: 'user@example.com',
//     id: 'USER123'
//   };

//   // Track profile section load
//   useEffect(() => {
//     trackKYCEvent('profile_section_loaded', {
//       activeTab: activeTab,
//       kycStatus: userStatus?.kycStatus || 'unknown',
//       clientId: currentUser?.id || currentUser?.clientId
//     });
//   }, [activeTab, userStatus?.kycStatus]);

//   // Get the correct client ID
//   const getActualClientId = () => {
//     if (userStatus?.clientId) {
//       return userStatus.clientId;
//     }
//     if (currentUser?.temp_client_id) {
//       return currentUser.temp_client_id;
//     }
//     if (currentUser?.['Id no']) {
//       return currentUser['Id no'];
//     }
//     return currentUser?.id;
//   };
  
//   // Profile form data initialized from props
//   const [profileData, setProfileData] = useState({
//     firstName: currentUser.name?.split(' ')[0] || '',
//     lastName: currentUser.name?.split(' ').slice(1).join(' ') || '',
//     email: currentUser.email || '',
//     phone: '',
//     address: ''
//   });
  
//   // KYC form data
//   const [kycData, setKycData] = useState({
//     kycType: '',
//     customKycType: '',
//     kycDocument: null,
//     countryCode: '+1',
//     isDrawing: false,
//     lastX: 0,
//     lastY: 0,
//     hasSignature: false,
//     uploadedSignature: null,
//     termsAccepted: false,
//     signatureData: null,
//     addressProofDocuments: [],
//     idProofDocuments: []
//   });
  
//   const [errors, setErrors] = useState({});
  
//   // Refs
//   const fileInputRef = useRef(null);
//   const signatureInputRef = useRef(null);
//   const signatureCanvasRef = useRef(null);

//   const [fullNameInput, setFullNameInput] = useState(`${profileData.firstName} ${profileData.lastName}`.trim());

//   // Initialize canvas when component mounts
//   useEffect(() => {
//     const canvas = signatureCanvasRef.current;
//     if (canvas && activeTab === 'kyc') {
//       const ctx = canvas.getContext('2d');
//       ctx.strokeStyle = '#000';
//       ctx.lineWidth = 2;
//       ctx.lineCap = 'round';
//       ctx.lineJoin = 'round';
      
//       // Set canvas size properly to avoid coordinate issues
//       const rect = canvas.getBoundingClientRect();
//       canvas.width = rect.width * window.devicePixelRatio;
//       canvas.height = rect.height * window.devicePixelRatio;
//       ctx.scale(window.devicePixelRatio, window.devicePixelRatio);
      
//       // Reapply styles after scaling
//       ctx.strokeStyle = '#000';
//       ctx.lineWidth = 2;
//       ctx.lineCap = 'round';
//       ctx.lineJoin = 'round';
//     }
//   }, [activeTab]);

//   // Handle tab switching with tracking
//   const handleTabSwitch = (tab) => {
//     trackKYCEvent('tab_switched', {
//       from: activeTab,
//       to: tab,
//       kycStatus: userStatus?.kycStatus || 'unknown'
//     });
//     setActiveTab(tab);
//   };

//   // Get KYC status
//   const getKYCStatus = () => {
//     if (!userStatus) return 'not_submitted';
//     return userStatus.kycStatus || 'not_submitted';
//   };

//   // Get rejection comments if status is rejected
//   const getRejectionComments = () => {
//     if (userStatus?.kycStatus === 'rejected' && userStatus?.rejectionComments) {
//       return userStatus.rejectionComments;
//     }
//     return null;
//   };

//   const getKYCStatusInfo = () => {
//     const status = getKYCStatus();
//     switch (status) {
//       case 'not_submitted':
//         return {
//           title: 'Complete your KYC verification to start investing',
//           color: 'text-[#B8B8B8]',
//           bgColor: 'bg-gradient-to-r from-[#676DFF]/10 to-[#676DFF]/5',
//           borderColor: 'border-[#676DFF]/20',
//           icon: AlertCircle,
//           canEdit: true,
//           buttonText: 'Complete KYC',
//           buttonBg: 'bg-[#676DFF]'
//         };
//       case 'submitted':
//       case 'pending':
//         return {
//           title: 'Your documents are being reviewed by our team',
//           color: 'text-[#676DFF]',
//           bgColor: 'bg-gradient-to-r from-[#676DFF]/10 to-[#676DFF]/5',
//           borderColor: 'border-[#676DFF]/20',
//           icon: Clock,
//           canEdit: false,
//           buttonText: 'Under Review',
//           buttonBg: 'bg-[#505050]'
//         };
//       case 'verified':
//         return {
//           title: 'Your identity has been successfully verified',
//           color: 'text-green-500',
//           bgColor: 'bg-gradient-to-r from-green-500/10 to-green-500/5',
//           borderColor: 'border-green-500/20',
//           icon: CheckCircle,
//           canEdit: false,
//           buttonText: 'Verified',
//           buttonBg: 'bg-green-500'
//         };
//       case 'rejected':
//         return {
//           title: 'Your KYC application has been rejected',
//           color: 'text-red-500',
//           bgColor: 'bg-gradient-to-r from-red-500/10 to-red-500/5',
//           borderColor: 'border-red-500/20',
//           icon: AlertCircle,
//           canEdit: true,
//           buttonText: 'View Details',
//           buttonBg: 'bg-red-500'
//         };
//       default:
//         return {
//           title: 'Please contact support for assistance',
//           color: 'text-[#B8B8B8]',
//           bgColor: 'bg-[#151515]/50',
//           borderColor: 'border-[#505050]',
//           icon: AlertCircle,
//           canEdit: false,
//           buttonText: 'Contact Support',
//           buttonBg: 'bg-[#505050]'
//         };
//     }
//   };

//   // Upload file to Supabase with error tracking
//   const uploadFileToSupabase = async (file, folder, fileName) => {
//     try {
//       let bucketName;
//       let filePath;
      
//       const actualClientId = getActualClientId();
      
//       // Use correct buckets based on file type
//       if (folder === 'documents') {
//         bucketName = 'kyc-docs';
//         filePath = `${actualClientId}/${fileName}`;
//       } else if (folder === 'signatures') {
//         bucketName = 'kyc-docs';
//         filePath = `signatures/${actualClientId}/${fileName}`;
//       } else if (folder === 'agreements') {
//         bucketName = 'agreement-pdfs';
//         filePath = `${actualClientId}/${fileName}`;
//       } else {
//         bucketName = 'kyc-docs';
//         filePath = `${folder}/${actualClientId}/${fileName}`;
//       }
      
//       console.log(`📁 Uploading to bucket: ${bucketName}, path: ${filePath}`);
      
//       const { data, error } = await supabase.storage
//         .from(bucketName)
//         .upload(filePath, file, {
//           cacheControl: '3600',
//           upsert: true
//         });

//       if (error) throw error;

//       // Get public URL
//       const { data: { publicUrl } } = supabase.storage
//         .from(bucketName)
//         .getPublicUrl(filePath);

//       console.log(`✅ File uploaded successfully: ${publicUrl}`);
      
//       trackKYCEvent('file_uploaded', {
//         folder,
//         fileName,
//         fileSize: file.size,
//         bucketName,
//         clientId: actualClientId
//       });
      
//       return publicUrl;
//     } catch (error) {
//       console.error('Error uploading file:', error);
      
//       trackDashboardError('file_upload', error, {
//         folder,
//         fileName,
//         fileSize: file?.size,
//         clientId: getActualClientId()
//       });
      
//       throw error;
//     }
//   };

//   const handleFileUpload = (documentType, file) => {
//     if (!file) return;
    
//     const validation = validateFile(file, 'documents');
//     if (!validation.isValid) {
//       setErrors(prev => ({ ...prev, [documentType]: validation.error }));
//       trackKYCEvent('document_upload_validation_failed', { 
//         documentType, 
//         error: validation.error 
//       });
//       return;
//     }
    
//     // Handle array-based document types
//     if (documentType === 'addressProofDocuments' || documentType === 'idProofDocuments') {
//       setKycData(prev => {
//         const currentDocs = prev[documentType] || [];
//         if (currentDocs.length >= 2) {
//           const error = 'Maximum 2 documents allowed. Please remove a document before adding a new one.';
//           setErrors(prevErr => ({
//             ...prevErr,
//             [documentType]: error
//           }));
//           trackKYCEvent('document_upload_limit_exceeded', { documentType });
//           return prev;
//         }
        
//         // Check for duplicate file names
//         const isDuplicate = currentDocs.some(doc => doc.name === file.name);
//         if (isDuplicate) {
//           const error = `A file named "${file.name}" has already been uploaded. Please rename the file or choose a different one.`;
//           setErrors(prevErr => ({
//             ...prevErr,
//             [documentType]: error
//           }));
//           trackKYCEvent('document_upload_duplicate', { documentType, fileName: file.name });
//           return prev;
//         }
        
//         // Clear errors and add file
//         setErrors(prevErr => ({ ...prevErr, [documentType]: '' }));
//         trackKYCEvent('document_uploaded', { 
//           documentType, 
//           fileName: file.name, 
//           fileSize: file.size,
//           fileType: file.type 
//         });
//         return {
//           ...prev,
//           [documentType]: [...currentDocs, file]
//         };
//       });
//     }
//   };

//   const removeFile = (documentType, index = null) => {
//     setKycData(prev => {
//       const currentDocs = prev[documentType] || [];
//       const removedFile = currentDocs[index];
      
//       trackKYCEvent('document_removed', { 
//         documentType, 
//         fileName: removedFile?.name,
//         index 
//       });
      
//       return {
//         ...prev,
//         [documentType]: currentDocs.filter((_, i) => i !== index)
//       };
//     });
//   };

//   // Signature canvas handlers
//   const getCanvasCoordinates = (canvas, clientX, clientY) => {
//     const rect = canvas.getBoundingClientRect();
//     return {
//       x: (clientX - rect.left) * (canvas.width / rect.width) / window.devicePixelRatio,
//       y: (clientY - rect.top) * (canvas.height / rect.height) / window.devicePixelRatio
//     };
//   };

//   const startDrawing = (e) => {
//     const canvas = signatureCanvasRef.current;
//     const coords = getCanvasCoordinates(canvas, e.clientX, e.clientY);
    
//     setKycData(prev => ({
//       ...prev,
//       isDrawing: true,
//       lastX: coords.x,
//       lastY: coords.y
//     }));
    
//     trackKYCEvent('signature_drawing_started', { method: 'mouse' });
//   };

//   const draw = (e) => {
//     if (!kycData.isDrawing) return;
    
//     const canvas = signatureCanvasRef.current;
//     const ctx = canvas.getContext('2d');
//     const coords = getCanvasCoordinates(canvas, e.clientX, e.clientY);
    
//     ctx.beginPath();
//     ctx.moveTo(kycData.lastX, kycData.lastY);
//     ctx.lineTo(coords.x, coords.y);
//     ctx.stroke();
    
//     setKycData(prev => ({
//       ...prev,
//       lastX: coords.x,
//       lastY: coords.y,
//       hasSignature: true
//     }));
//   };

//   const stopDrawing = () => {
//     setKycData(prev => ({ ...prev, isDrawing: false }));
//     trackKYCEvent('signature_drawing_ended', { method: 'mouse' });
//   };

//   const clearSignature = () => {
//     const canvas = signatureCanvasRef.current;
//     const ctx = canvas.getContext('2d');
//     ctx.clearRect(0, 0, canvas.width, canvas.height);
//     setKycData(prev => ({ 
//       ...prev, 
//       hasSignature: false, 
//       uploadedSignature: null,
//       signatureData: null
//     }));
//     trackKYCEvent('signature_cleared');
//   };

//   const handleSignatureUpload = (file) => {
//     if (!file) return;
    
//     const validation = validateFile(file, 'signature');
//     if (!validation.isValid) {
//       setErrors(prev => ({ ...prev, signature: validation.error }));
//       trackKYCEvent('signature_upload_validation_failed', { error: validation.error });
//       return;
//     }
    
//     const img = new Image();
//     img.onload = () => {
//       if (img.width < 100 || img.height < 50) {
//         const error = 'Signature image is too small. Minimum size: 100x50 pixels';
//         setErrors(prev => ({ ...prev, signature: error }));
//         trackKYCEvent('signature_upload_too_small', { width: img.width, height: img.height });
//         return;
//       }
      
//       const reader = new FileReader();
//       reader.onload = () => {
//         setKycData(prev => ({ 
//           ...prev, 
//           uploadedSignature: file,
//           signatureData: reader.result,
//           hasSignature: true
//         }));
//         setErrors(prev => ({ ...prev, signature: '' }));
//         trackKYCEvent('signature_uploaded', { 
//           fileName: file.name,
//           fileSize: file.size,
//           dimensions: `${img.width}x${img.height}`
//         });
//       };
//       reader.readAsDataURL(file);
//     };
//     img.src = URL.createObjectURL(file);
//   };

//   // Get signature data for submission
//   const getSignatureData = () => {
//     if (kycData.uploadedSignature && kycData.signatureData) {
//       return kycData.signatureData;
//     }
    
//     if (kycData.hasSignature && signatureCanvasRef.current) {
//       return signatureCanvasRef.current.toDataURL('image/png');
//     }
    
//     return null;
//   };

//   // Form validation with tracking
//   const validateForm = () => {
//     const newErrors = {};
    
//     trackKYCEvent('form_validation_started');
    
//     // Basic validation
//     if (!profileData.firstName.trim()) {
//       newErrors.firstName = 'First name is required';
//     }
    
//     if (!profileData.lastName.trim()) {
//       newErrors.lastName = 'Last name is required';
//     }
    
//     if (!profileData.email.trim()) {
//       newErrors.email = 'Email address is required';
//     } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(profileData.email.trim())) {
//       newErrors.email = 'Please enter a valid email address';
//     }
    
//     if (!profileData.phone.trim()) {
//       newErrors.phone = 'Phone number is required';
//     }
    
//     if (!profileData.address.trim()) {
//       newErrors.address = 'Residential address is required';
//     }
    
//     // Document validation
//     if (!kycData.addressProofDocuments || kycData.addressProofDocuments.length === 0) {
//       newErrors.addressProofDocuments = 'At least one address proof document is required';
//     }
    
//     if (!kycData.idProofDocuments || kycData.idProofDocuments.length === 0) {
//       newErrors.idProofDocuments = 'At least one ID proof document is required';
//     }
    
//     // Signature validation
//     const signatureData = getSignatureData();
//     if (!kycData.hasSignature && !signatureData) {
//       newErrors.signature = 'Digital signature is required';
//     }
    
//     // Terms validation
//     if (!kycData.termsAccepted) {
//       newErrors.terms = 'You must read and accept the terms and conditions';
//     }
    
//     setErrors(newErrors);
    
//     const isValid = Object.keys(newErrors).length === 0;
//     trackKYCEvent('form_validation_completed', {
//       isValid,
//       errorCount: Object.keys(newErrors).length,
//       errors: Object.keys(newErrors)
//     });
    
//     return isValid;
//   };

//   // Generate PDF
//   const generateAgreementPDF = async () => {
//     try {
//       const formDataForPdf = {
//         fullName: `${profileData.firstName} ${profileData.lastName}`.trim(),
//         email: profileData.email,
//         phone: `${kycData.countryCode}${profileData.phone}`,
//         address: profileData.address,
//         effectiveDate: new Date().toLocaleDateString('en-GB', {
//           day: 'numeric', month: 'long', year: 'numeric'
//         })
//       };
      
//       const signatureData = getSignatureData();
      
//       const pdfBlob = await makeAgreementPdf({
//         formData: formDataForPdf,
//         signatureData: signatureData,
//         signatureIsUpload: !!kycData.uploadedSignature
//       });
      
//       trackKYCEvent('agreement_pdf_generated', {
//         clientId: getActualClientId(),
//         hasSignature: !!signatureData
//       });
      
//       return pdfBlob;
//     } catch (error) {
//       console.error('Error generating PDF:', error);
//       trackDashboardError('pdf_generation', error, {
//         clientId: getActualClientId()
//       });
//       throw error;
//     }
//   };

//   // MAIN KYC SUBMISSION HANDLER
//   const handleKYCSubmit = async () => {
//     console.log('🚀 KYC Submit button clicked');
    
//     trackKYCEvent('kyc_submission_started', {
//       clientId: getActualClientId(),
//       kycStatus: getKYCStatus(),
//       hasAddressProof: kycData.addressProofDocuments.length,
//       hasIdProof: kycData.idProofDocuments.length,
//       hasSignature: kycData.hasSignature
//     });
    
//     setErrors(prev => ({ ...prev, submit: '' }));

//     if (!validateForm()) {
//       setErrors(prev => ({ 
//         ...prev, 
//         submit: 'Please fix the errors above before submitting' 
//       }));
      
//       trackKYCEvent('kyc_submission_validation_failed', {
//         errorCount: Object.keys(errors).length,
//         errors: Object.keys(errors)
//       });
//       return;
//     }
    
//     setIsSubmitting(true);
    
//     try {
//       const actualClientId = getActualClientId();
//       console.log('📁 Starting KYC submission for client:', actualClientId);
      
//       const token = localStorage.getItem('token');
//       if (!token) {
//         throw new Error('No authentication token found. Please log in again.');
//       }
      
//       // 1. Upload Address Proof documents
//       console.log('📤 Uploading address proof documents...');
//       const addressProofUrls = [];
//       for (let i = 0; i < kycData.addressProofDocuments.length; i++) {
//         const doc = kycData.addressProofDocuments[i];
//         const url = await uploadFileToSupabase(
//           doc,
//           'documents',
//           `address_proof_${i + 1}_${Date.now()}.${doc.name.split('.').pop()}`
//         );
//         addressProofUrls.push(url);
//       }
      
//       // 2. Upload ID Proof documents
//       console.log('📤 Uploading ID proof documents...');
//       const idProofUrls = [];
//       for (let i = 0; i < kycData.idProofDocuments.length; i++) {
//         const doc = kycData.idProofDocuments[i];
//         const url = await uploadFileToSupabase(
//           doc,
//           'documents',
//           `id_proof_${i + 1}_${Date.now()}.${doc.name.split('.').pop()}`
//         );
//         idProofUrls.push(url);
//       }
      
//       // 3. Upload signature
//       console.log('📤 Processing signature...');
//       const signatureData = getSignatureData();
//       let signatureUrl = null;
      
//       if (signatureData) {
//         const response = await fetch(signatureData);
//         const blob = await response.blob();
        
//         signatureUrl = await uploadFileToSupabase(
//           blob,
//           'signatures',
//           `signature_${Date.now()}.png`
//         );
//       }
      
//       // 4. Generate and upload PDF
//       console.log('📄 Generating PDF...');
//       const pdfBlob = await generateAgreementPDF();
//       const pdfUrl = await uploadFileToSupabase(
//         pdfBlob,
//         'agreements',
//         `agreement_${Date.now()}.pdf`
//       );
      
//       // 5. Prepare submission data
//       const submissionData = {
//         clientId: actualClientId,
//         fullname: `${profileData.firstName} ${profileData.lastName}`.trim(),
//         email: profileData.email,
//         countrycode: kycData.countryCode,
//         phone: profileData.phone,
//         address: profileData.address,
//         effectivedate: new Date().toISOString().split('T')[0],
//         signature: signatureUrl,
//         kyctype: 'digital_kyc',
//         address_proof_urls: JSON.stringify(addressProofUrls),
//         id_proof_urls: JSON.stringify(idProofUrls),
//         agreement_pdf_url: pdfUrl
//       };
      
//       // 6. Submit to backend API
//       try {
//         const backendUrl = process.env.REACT_APP_API_URL || 'https://app-abnexus.onrender.com';
//         const apiUrl = `${backendUrl}/api/submit-kyc`;
        
//         const response = await fetch(apiUrl, {
//           method: 'POST',
//           headers: {
//             'Content-Type': 'application/json',
//             'Authorization': `Bearer ${token}`
//           },
//           body: JSON.stringify(submissionData)
//         });
        
//         if (response.ok) {
//           trackKYCEvent('api_submission_success', { clientId: actualClientId });
//         } else {
//           throw new Error('API submission failed');
//         }
//       } catch (apiError) {
//         // Fallback to direct database update
//         console.log('📁 API failed, attempting direct database update');
        
//         const kycRecord = {
//           client_id: actualClientId,
//           fullname: submissionData.fullname,
//           email: submissionData.email,
//           countrycode: submissionData.countrycode,
//           phone: submissionData.phone,
//           address: submissionData.address,
//           effectivedate: submissionData.effectivedate,
//           signature: submissionData.signature,
//           kyctype: submissionData.kyctype,
//           address_proof_urls: submissionData.address_proof_urls,
//           id_proof_urls: submissionData.id_proof_urls,
//           agreement_pdf_url: submissionData.agreement_pdf_url,
//           status: 'pending',
//           created_at: new Date().toISOString(),
//           updated_at: new Date().toISOString()
//         };
        
//         const { error } = await supabase
//           .from('agreements_pending')
//           .upsert(kycRecord, { 
//             onConflict: 'client_id',
//             ignoreDuplicates: false 
//           });
        
//         if (error) {
//           throw new Error('Failed to save KYC data. Please contact support.');
//         }
//       }
      
//       // Success
//       setShowSuccessMessage(true);
      
//       trackKYCEvent('kyc_submission_completed', {
//         clientId: actualClientId,
//         totalDocuments: addressProofUrls.length + idProofUrls.length
//       });
      
//       // Update user status
//       if (onStatusUpdate) {
//         await onStatusUpdate();
//       }
      
//       setTimeout(() => {
//         setActiveTab('profile');
//         setShowSuccessMessage(false);
//       }, 3000);
      
//     } catch (error) {
//       console.error('❌ KYC Submission Error:', error);
//       setErrors({ 
//         submit: 'An error occurred during submission. Please try again or contact support.' 
//       });
      
//       trackKYCEvent('kyc_submission_failed', {
//         clientId: getActualClientId(),
//         error: error.message
//       });
//     } finally {
//       setIsSubmitting(false);
//     }
//   };

//   const kycStatusInfo = getKYCStatusInfo();

//   return (
//     <div className="min-h-screen bg-[#0A0A0A] text-white font-['Lexend']">
//       {/* Success Message Overlay */}
//       {showSuccessMessage && (
//         <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
//           <div className="bg-[#151515] border border-green-500/30 rounded-xl p-8 max-w-md w-full text-center shadow-2xl">
//             <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
//               <CheckCircle className="h-8 w-8 text-green-500" />
//             </div>
//             <h3 className="text-xl font-semibold text-white mb-2">KYC Submitted Successfully!</h3>
//             <p className="text-[#B8B8B8] text-sm mb-4">
//               Your documents have been submitted for review. Our team will process your application within 1-3 business days.
//             </p>
//           </div>
//         </div>
//       )}

//       <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-8">
//         {/* Tab Navigation */}
//         <div className="mb-6 sm:mb-8">
//           <div className="border-b border-[#151515]">
//             <nav className="flex space-x-8">
//               <button
//                 onClick={() => handleTabSwitch('profile')}
//                 className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors duration-200 ${
//                   activeTab === 'profile'
//                     ? 'border-[#676DFF] text-[#676DFF]'
//                     : 'border-transparent text-[#B8B8B8] hover:text-white hover:border-[#505050]'
//                 }`}
//               >
//                 Profile Information
//               </button>
//               <button
//                 onClick={() => handleTabSwitch('kyc')}
//                 className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors duration-200 ${
//                   activeTab === 'kyc'
//                     ? 'border-[#676DFF] text-[#676DFF]'
//                     : 'border-transparent text-[#B8B8B8] hover:text-white hover:border-[#505050]'
//                 } flex items-center`}
//               >
//                 KYC Verification
//                 {getKYCStatus() === 'not_submitted' && (
//                   <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-[#676DFF] text-white">
//                     Required
//                   </span>
//                 )}
//               </button>
//             </nav>
//           </div>
//         </div>

//         {/* Tab Content */}
//         {activeTab === 'profile' ? (
//           <div className="space-y-6 sm:space-y-8">
//             {/* Profile Information Card */}
//             <div className="bg-[#151515] border border-[#505050]/20 rounded-lg p-4 sm:p-6">
//               <div className="flex items-center space-x-3 mb-6">
//                 <User className="h-5 w-5 text-[#676DFF]" />
//                 <h2 className="text-lg font-semibold">Profile Information</h2>
//               </div>

//               <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
//                 <div>
//                   <label className="block text-sm font-medium text-[#B8B8B8] mb-2">First Name</label>
//                   <div className="p-3 bg-[#0A0A0A] border border-[#505050]/20 rounded-md text-white">
//                     {profileData.firstName || 'Not provided'}
//                   </div>
//                 </div>

//                 <div>
//                   <label className="block text-sm font-medium text-[#B8B8B8] mb-2">Last Name</label>
//                   <div className="p-3 bg-[#0A0A0A] border border-[#505050]/20 rounded-md text-white">
//                     {profileData.lastName || 'Not provided'}
//                   </div>
//                 </div>

//                 <div>
//                   <label className="block text-sm font-medium text-[#B8B8B8] mb-2">Email ID</label>
//                   <div className="p-3 bg-[#0A0A0A] border border-[#505050]/20 rounded-md text-white break-all">
//                     {profileData.email || 'Not provided'}
//                   </div>
//                 </div>

//                 <div>
//                   <label className="block text-sm font-medium text-[#B8B8B8] mb-2">Phone Number</label>
//                   <div className="p-3 bg-[#0A0A0A] border border-[#505050]/20 rounded-md text-white flex items-center">
//                     <Phone className="h-4 w-4 mr-2 text-[#808080] flex-shrink-0" />
//                     <span className="break-all">{profileData.phone || 'Not provided'}</span>
//                   </div>
//                 </div>

//                 <div className="sm:col-span-2">
//                   <label className="block text-sm font-medium text-[#B8B8B8] mb-2">Residential Address</label>
//                   <div className="p-3 bg-[#0A0A0A] border border-[#505050]/20 rounded-md text-white flex items-start">
//                     <MapPin className="h-4 w-4 mr-2 text-[#808080] mt-0.5 flex-shrink-0" />
//                     <span className="break-words">{profileData.address || 'Not provided'}</span>
//                   </div>
//                 </div>
//               </div>
//             </div>

//             {/* Account Status Card */}
//             <div className="bg-[#151515] border border-[#505050]/20 rounded-lg p-4 sm:p-6">
//               <h2 className="text-lg font-semibold mb-4 sm:mb-6">Account Status</h2>
              
//               <div className={`${kycStatusInfo.bgColor} border ${kycStatusInfo.borderColor} rounded-lg p-4 mb-4 sm:mb-6 backdrop-blur-sm`}>
//                 <div className="flex items-center space-x-3">
//                   <div className="p-2 rounded-full bg-[#676DFF]/10 flex-shrink-0">
//                     <kycStatusInfo.icon className={`h-4 w-4 ${kycStatusInfo.color}`} />
//                   </div>
//                   <div className="min-w-0 flex-1">
//                     <p className="font-medium text-white">
//                       {getKYCStatus() === 'verified' ? 'KYC Verification Complete' : 
//                        getKYCStatus() === 'submitted' || getKYCStatus() === 'pending' ? 'KYC Under Review' : 
//                        getKYCStatus() === 'rejected' ? 'KYC Application Rejected' :
//                        'KYC Verification Required'}
//                     </p>
//                     <p className={`text-sm ${kycStatusInfo.color} break-words`}>{kycStatusInfo.title}</p>
//                   </div>
//                 </div>
//               </div>
              
//               <div className="grid grid-cols-1 gap-4 sm:gap-6 text-sm">
//                 <div>
//                   <p className="text-[#B8B8B8] mb-1">Client ID</p>
//                   <p className="font-medium text-white break-all">
//                     {getActualClientId() || 'Pending'}
//                   </p>
//                 </div>
//               </div>
//             </div>
//           </div>
//         ) : (
//           // KYC Tab Content
//           <div className="space-y-6 sm:space-y-8">
//             {getKYCStatus() === 'verified' ? (
//               <div className="bg-[#151515] border border-[#505050]/20 rounded-lg p-6 sm:p-8 text-center">
//                 <CheckCircle className="h-12 w-12 sm:h-16 sm:w-16 text-green-500 mx-auto mb-4" />
//                 <h3 className="text-lg sm:text-xl font-semibold mb-2 text-white">KYC Verification Complete</h3>
//                 <p className="text-[#B8B8B8] mb-6 text-sm sm:text-base">
//                   Your identity has been successfully verified. You can now access all investment features.
//                 </p>
//               </div>
//             ) : getKYCStatus() === 'submitted' || getKYCStatus() === 'pending' ? (
//               <div className="bg-[#151515] border border-[#505050]/20 rounded-lg p-6 sm:p-8 text-center">
//                 <Clock className="h-12 w-12 sm:h-16 sm:w-16 text-[#676DFF] mx-auto mb-4" />
//                 <h3 className="text-lg sm:text-xl font-semibold mb-2 text-white">KYC Under Review</h3>
//                 <p className="text-[#B8B8B8] mb-6 text-sm sm:text-base">
//                   Thank you for submitting your documents. Our team is reviewing your application.
//                 </p>
//               </div>
//             ) : (
//               // KYC Form
//               <div className="bg-[#151515] border border-[#505050]/20 rounded-lg p-4 sm:p-6">
//                 <h2 className="text-lg font-semibold mb-4 sm:mb-6 text-white">KYC Verification</h2>
                
//                 <div className="space-y-4 sm:space-y-6">
//                   {/* Full Name */}
//                   <div>
//                     <label className="block text-sm font-medium text-[#B8B8B8] mb-2">Full Name *</label>
//                     <input
//                       type="text"
//                       value={fullNameInput}
//                       onChange={(e) => {
//                         setFullNameInput(e.target.value);
//                         const names = e.target.value.trim().split(/\s+/);
//                         setProfileData(prev => ({
//                           ...prev,
//                           firstName: names[0] || '',
//                           lastName: names.slice(1).join(' ') || ''
//                         }));
//                       }}
//                       className="w-full p-3 bg-[#0A0A0A] border border-[#505050] rounded-md focus:ring-2 focus:ring-[#676DFF] focus:border-[#676DFF] text-white placeholder-[#808080] transition-colors duration-200"
//                       placeholder="Enter your full name"
//                       required
//                     />
//                     {errors.firstName && <p className="text-red-400 text-sm mt-1">{errors.firstName}</p>}
//                   </div>

//                   {/* Email ID */}
//                   <div>
//                     <label className="block text-sm font-medium text-[#B8B8B8] mb-2">Email ID *</label>
//                     <input
//                       type="email"
//                       value={profileData.email}
//                       onChange={(e) => setProfileData(prev => ({ ...prev, email: e.target.value }))}
//                       className="w-full p-3 bg-[#0A0A0A] border border-[#505050] rounded-md focus:ring-2 focus:ring-[#676DFF] focus:border-[#676DFF] text-white placeholder-[#808080] transition-colors duration-200"
//                       placeholder="Enter your email address"
//                       required
//                     />
//                     {errors.email && <p className="text-red-400 text-sm mt-1">{errors.email}</p>}
//                   </div>

//                   {/* Phone Number */}
//                   <div>
//                     <label className="block text-sm font-medium text-[#B8B8B8] mb-2">Phone Number *</label>
//                     <input
//                       type="tel"
//                       value={profileData.phone}
//                       onChange={(e) => setProfileData(prev => ({ ...prev, phone: e.target.value }))}
//                       className="w-full p-3 bg-[#0A0A0A] border border-[#505050] rounded-md focus:ring-2 focus:ring-[#676DFF] focus:border-[#676DFF] text-white placeholder-[#808080] transition-colors duration-200"
//                       placeholder="Enter your phone number"
//                       required
//                     />
//                     {errors.phone && <p className="text-red-400 text-sm mt-1">{errors.phone}</p>}
//                   </div>

//                   {/* Address */}
//                   <div>
//                     <label className="block text-sm font-medium text-[#B8B8B8] mb-2">Residential Address *</label>
//                     <textarea
//                       value={profileData.address}
//                       onChange={(e) => setProfileData(prev => ({ ...prev, address: e.target.value }))}
//                       rows={3}
//                       className="w-full p-3 bg-[#0A0A0A] border border-[#505050] rounded-md focus:ring-2 focus:ring-[#676DFF] focus:border-[#676DFF] text-white placeholder-[#808080] transition-colors duration-200 resize-none"
//                       placeholder="Enter your residential address"
//                       required
//                     />
//                     {errors.address && <p className="text-red-400 text-sm mt-1">{errors.address}</p>}
//                   </div>

//                   {/* Address Proof Documents */}
//                   <div>
//                     <label className="block text-sm font-medium text-[#B8B8B8] mb-2">Address Proof Documents *</label>
//                     <div className="border-2 border-dashed border-[#505050] rounded-lg p-4 sm:p-6 text-center">
//                       <Upload className="h-6 w-6 sm:h-8 sm:w-8 text-[#808080] mx-auto mb-2" />
//                       <p className="text-sm text-[#B8B8B8] mb-2">Upload address proof documents</p>
//                       <input
//                         type="file"
//                         accept=".jpg,.jpeg,.png,.pdf"
//                         onChange={(e) => handleFileUpload('addressProofDocuments', e.target.files[0])}
//                         className="hidden"
//                         id="address-proof-upload"
//                       />
//                       <label htmlFor="address-proof-upload">
//                         <button
//                           type="button"
//                           className="px-4 sm:px-6 py-2 bg-[#676DFF] hover:bg-[#676DFF]/90 text-white rounded-md text-sm font-medium transition-colors duration-200"
//                           onClick={() => document.getElementById('address-proof-upload')?.click()}
//                         >
//                           Upload Documents
//                         </button>
//                       </label>
                      
//                       {kycData.addressProofDocuments && kycData.addressProofDocuments.length > 0 && (
//                         <div className="mt-4 space-y-2">
//                           {kycData.addressProofDocuments.map((file, index) => (
//                             <div key={index} className="flex items-center justify-between bg-[#0A0A0A] p-2 rounded border border-[#505050]">
//                               <span className="text-sm text-white break-all flex-1">{file.name}</span>
//                               <button
//                                 onClick={() => removeFile('addressProofDocuments', index)}
//                                 className="text-red-400 hover:text-red-300 text-sm ml-2 flex-shrink-0"
//                               >
//                                 <X className="h-4 w-4" />
//                               </button>
//                             </div>
//                           ))}
//                         </div>
//                       )}
//                     </div>
//                     {errors.addressProofDocuments && <p className="text-red-400 text-sm mt-1">{errors.addressProofDocuments}</p>}
//                   </div>

//                   {/* ID Proof Documents */}
//                   <div>
//                     <label className="block text-sm font-medium text-[#B8B8B8] mb-2">ID Proof Documents *</label>
//                     <div className="border-2 border-dashed border-[#505050] rounded-lg p-4 sm:p-6 text-center">
//                       <Upload className="h-6 w-6 sm:h-8 sm:w-8 text-[#808080] mx-auto mb-2" />
//                       <p className="text-sm text-[#B8B8B8] mb-2">Upload ID proof documents</p>
//                       <input
//                         type="file"
//                         accept=".jpg,.jpeg,.png,.pdf"
//                         onChange={(e) => handleFileUpload('idProofDocuments', e.target.files[0])}
//                         className="hidden"
//                         id="id-proof-upload"
//                       />
//                       <label htmlFor="id-proof-upload">
//                         <button
//                           type="button"
//                           className="px-4 sm:px-6 py-2 bg-[#676DFF] hover:bg-[#676DFF]/90 text-white rounded-md text-sm font-medium transition-colors duration-200"
//                           onClick={() => document.getElementById('id-proof-upload')?.click()}
//                         >
//                           Upload Documents
//                         </button>
//                       </label>
                      
//                       {kycData.idProofDocuments && kycData.idProofDocuments.length > 0 && (
//                         <div className="mt-4 space-y-2">
//                           {kycData.idProofDocuments.map((file, index) => (
//                             <div key={index} className="flex items-center justify-between bg-[#0A0A0A] p-2 rounded border border-[#505050]">
//                               <span className="text-sm text-white break-all flex-1">{file.name}</span>
//                               <button
//                                 onClick={() => removeFile('idProofDocuments', index)}
//                                 className="text-red-400 hover:text-red-300 text-sm ml-2 flex-shrink-0"
//                               >
//                                 <X className="h-4 w-4" />
//                               </button>
//                             </div>
//                           ))}
//                         </div>
//                       )}
//                     </div>
//                     {errors.idProofDocuments && <p className="text-red-400 text-sm mt-1">{errors.idProofDocuments}</p>}
//                   </div>

//                   {/* Signature Canvas */}
//                   <div>
//                     <h3 className="text-base sm:text-lg font-medium text-white mb-4">Digital Signature *</h3>
//                     <div className="bg-white border border-[#505050] rounded-lg p-2 sm:p-4">
//                       <canvas
//                         ref={signatureCanvasRef}
//                         className="w-full border border-gray-300 rounded cursor-crosshair"
//                         onMouseDown={startDrawing}
//                         onMouseMove={draw}
//                         onMouseUp={stopDrawing}
//                         onMouseLeave={stopDrawing}
//                         style={{ width: '100%', height: '150px' }}
//                       />
//                     </div>
//                     <div className="flex space-x-3 mt-3">
//                       <button
//                         type="button"
//                         onClick={clearSignature}
//                         className="px-4 py-2 bg-[#505050] hover:bg-[#505050]/80 text-white rounded-md text-sm transition-colors duration-200"
//                       >
//                         Clear Signature
//                       </button>
//                     </div>
//                     {errors.signature && <p className="text-red-400 text-sm mt-1">{errors.signature}</p>}
//                   </div>

//                   {/* Upload Signature Alternative */}
//                   <div>
//                     <label className="block text-sm font-medium text-[#B8B8B8] mb-2">Upload Signature (Alternative)</label>
//                     <div className="border-2 border-dashed border-[#505050] rounded-lg p-4 sm:p-6 text-center">
//                       <Upload className="h-6 w-6 sm:h-8 sm:w-8 text-[#808080] mx-auto mb-2" />
//                       <p className="text-sm text-[#B8B8B8] mb-2">
//                         {kycData.uploadedSignature ? 
//                           `Selected: ${kycData.uploadedSignature.name}` : 
//                           'Click to upload your signature (JPG or PNG)'
//                         }
//                       </p>
//                       <input
//                         type="file"
//                         accept=".jpg,.jpeg,.png"
//                         onChange={(e) => handleSignatureUpload(e.target.files[0])}
//                         className="hidden"
//                         id="signature-upload"
//                         ref={signatureInputRef}
//                       />
//                       <label htmlFor="signature-upload">
//                         <button
//                           type="button"
//                           className="px-4 sm:px-6 py-2 bg-[#676DFF] hover:bg-[#676DFF]/90 text-white rounded-md text-sm font-medium transition-colors duration-200"
//                           onClick={() => signatureInputRef.current?.click()}
//                         >
//                           Upload Signature
//                         </button>
//                       </label>
//                     </div>
//                   </div>

//                   {/* Terms and Conditions */}
//                   <div className="space-y-4 pt-4 sm:pt-6 border-t border-[#505050]/20">
//                     <label className="flex items-start space-x-3 cursor-pointer">
//                       <input
//                         type="checkbox"
//                         checked={kycData.termsAccepted}
//                         onChange={(e) => setKycData(prev => ({ ...prev, termsAccepted: e.target.checked }))}
//                         className="w-4 h-4 text-[#676DFF] bg-[#0A0A0A] border-[#505050] rounded focus:ring-[#676DFF] focus:ring-2 mt-0.5 flex-shrink-0"
//                       />
//                       <span className="text-sm text-white">I have read and accept the terms and conditions</span>
//                     </label>
//                     {errors.terms && <p className="text-red-400 text-sm mt-1">{errors.terms}</p>}
//                   </div>

//                   {/* Submit Button */}
//                   <div className="pt-4">
//                     <button
//                       onClick={handleKYCSubmit}
//                       disabled={!kycData.termsAccepted || isSubmitting}
//                       className="w-full py-3 bg-[#676DFF] hover:bg-[#676DFF]/90 disabled:bg-[#505050] disabled:cursor-not-allowed text-white rounded-md font-medium transition-colors duration-200"
//                     >
//                       {isSubmitting ? 'Submitting Agreement...' : 'Submit Agreement'}
//                     </button>
//                     {errors.submit && <p className="text-red-400 text-sm mt-2">{errors.submit}</p>}
//                   </div>
//                 </div>
//               </div>
//             )}
//           </div>
//         )}
//       </div>
//     </div>
//   );
// };

// export default ProfileSettings;